import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Building, Inspection, HeatSource, HeatSourceType, HeatCharacterType } from '../types';
import { buildingsApi, inspectionsApi, apartmentInspectionsApi, apartmentsApi, clientsApi } from '../services/api';
import type { BuildingProtocolEntry, Client } from '../services/api';
import type { InspectionUpdateError } from '../services/api';
import { ApartmentInspectionForm } from './ApartmentInspectionForm';
import { HeatSourceDetails } from './HeatSourceDetails';
import { HeatSourceEditModal } from './HeatSourceEditModal';
import { OtherDeficienciesNoteSection } from './OtherDeficienciesNoteSection';
import { UserCardsAttachmentSection } from './UserCardsAttachmentSection';
import { mapInspectionStatusToApartmentStatus } from '../types/inspection';
import { ApartmentHistoryModal } from './ApartmentHistoryModal';
import { formatDate } from '../utils/dateFormat';
import { attemptNumber, attemptSortKey, formatAttemptDate, formatAttemptTime, romanTerm } from '../utils/attempts';
import {
  HEAT_SOURCE_TYPE_LABELS,
  HEAT_SOURCE_TYPE_ORDER,
  HEAT_CHARACTER_LABELS,
  resolveHeatSourceType,
  resolveHeatCharacter,
  HEAT_TYPES_WITHOUT_CHARACTER,
} from '../ds/HeatSourceLabels';
import { ModalShell } from '../ds/ModalShell';
import { ConfirmModal } from '../ds/ConfirmModal';
import { termDateError, termDateBounds } from '../utils/termDate';
import { apartmentDeleteBlockReason } from '../utils/apartmentDelete';
import ApartmentDeleteWithRecordsModal from './ApartmentDeleteWithRecordsModal';
import { useAuthOptional } from '../contexts/AuthContext';
import { asciiFilename, saveBlob } from '../utils/download';
import { FlueWarningIcon } from './FlueWarningIcon';
import { SummaryHeatSourcesOption } from './SummaryHeatSourcesOption';
import { useSummaryHeatSources } from '../hooks/useSummaryHeatSources';
import { useProtocolPdfsPack } from '../hooks/useProtocolPdfsPack';
import type { ProtocolPackMessage } from '../hooks/useProtocolPdfsPack';
import { ProtocolYearSelect, currentProtocolYear } from './ProtocolYearSelect';
import { InputField } from '../ds/InputField';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { StatusChip } from '../ds/components/StatusChip';
import { statusToDS, statusLabel, hasVisitOutcome } from '../ds/StatusMap';
import { useCeebImportPolling } from '../hooks/useCeebImportPolling';
import CeebPhaseAPanel from './CeebPhaseAPanel';
import CeebLastRunReport from './CeebLastRunReport';
import { TabletHeldBanner } from './TabletHeldBanner';
import CeebUnlinkCasePanel from './CeebUnlinkCasePanel';
import { ceebDeletedFull, deletedDetectedLabel } from '../utils/ceebDeletedProtocol';
import UncheckedApartmentsModal from './UncheckedApartmentsModal';
import BulkAbsenceModal from './BulkAbsenceModal';
import ApartmentLabelsModal from './ApartmentLabelsModal';
import ApartmentRenumberModal from './ApartmentRenumberModal';
import { apartmentDoorMarking } from '../utils/apartmentLabels';
import { ceebCaseYear, ceebProtocolLabel, isBeforeCurrentYear } from '../utils/ceebProtocolLabel';
// Nagłówek typu wspólny z zestawieniem wielu budynków (również `unknown` → „Nieokreślony typ").
import { heatSourceColumnLabel } from '../utils/heatSourceSummary';
import {
  heatSourceParamsPayload,
  heatSourcesBaseOf,
  isHeatSourcesConflict,
  newWriteId,
  untouchedHeatSources,
} from '../utils/heatSourcesBase';

interface Props {
  buildingId: string;
  // W6: inspectionId=null = budynek BEZ zaplanowanego przeglądu (tryb read-only).
  inspectionId: string | null;
  onClose: () => void;
  /** W6: z trybu read-only (brak przeglądu) pozwala wskoczyć w „Zaplanuj przegląd"
   *  (PipelineView podaje callback zamykający widok i otwierający modal planowania). */
  onPlanInspection?: () => void;
  /** pkt 14: „Zakres kontroli" pusty w CEEB — flaga z wiersza listy (`flue_counts_missing`);
   *  GET /buildings/:id i /inspections/:id jej nie niosą. Brak = nie pokazujemy znacznika. */
  flueCountsMissing?: boolean;
}

const BuildingInspectionView: React.FC<Props> = ({ buildingId, inspectionId, onClose, onPlanInspection, flueCountsMissing = false }) => {
  const [building, setBuilding] = useState<Building | null>(null);
  const [inspection, setInspection] = useState<Inspection | null>(null);
  // F-min (§12–14): historia protokołów CEEB budynku (rok → nr → status).
  const [protocolHistory, setProtocolHistory] = useState<BuildingProtocolEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddHeatSourceModal, setShowAddHeatSourceModal] = useState(false);
  const [showAddApartmentModal, setShowAddApartmentModal] = useState(false);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyApartment, setHistoryApartment] = useState<{ id: string; number: string; address: string } | null>(null);
  // 035 „Dane właściciela/zarządcy" (issue #167) — do tej pory edytowalne WYŁĄCZNIE z tabletu.
  // Pola opcjonalne i bez walidacji — parytet z mobilką (BuildingDetailsScreen), która też
  // jej nie ma. Pusty string = jawne wyczyszczenie (backend COALESCE reaguje tylko na null).
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [ownerForm, setOwnerForm] = useState({ ownerSurname: '', ownerName: '', ownerEmail: '', clientId: '' });
  // Lista zarządców do selecta w modalu właściciela (ręczna korekta przypisania, 2026-08-10).
  const [zarzadcy, setZarzadcy] = useState<Client[]>([]);
  const [ownerSaving, setOwnerSaving] = useState(false);
  const [ownerError, setOwnerError] = useState<string | null>(null);
  // „Oznaczenie specjalne" (§16, 2026-08-04) — inline edycja w nagłówku. Czyszczenie = pusty
  // string (backend COALESCE reaguje tylko na null, jak przy danych właściciela).
  const [editingMarking, setEditingMarking] = useState(false);
  const [markingDraft, setMarkingDraft] = useState('');
  const [markingSaving, setMarkingSaving] = useState(false);
  const [markingError, setMarkingError] = useState<string | null>(null);
  const [heatSourceType, setHeatSourceType] = useState<HeatSourceType | ''>('');
  const [heatCharacter, setHeatCharacter] = useState<HeatCharacterType | ''>('');
  const [newApartmentNumber, setNewApartmentNumber] = useState('');
  const [editingApartment, setEditingApartment] = useState<any>(null);
  const [inspectionHistory, setInspectionHistory] = useState<any[]>([]);
  const [editingInspection, setEditingInspection] = useState(false);
  const [inspectionFormData, setInspectionFormData] = useState({
    firstTermDate: '',
    secondTermDate: '',
    notes: '',
    // 044: osoba uczestnicząca (teren) — nadpisuje mistrza w CEEB; '' = mistrz firmowy.
    participantName: '',
  });
  // DS: confirm-modal replaces window.confirm (preserves the confirm gate before destructive actions)
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmLabel?: string;
  }>({ open: false, title: '', message: '', onConfirm: () => {} });

  // confirmLabel: domyślnie „Usuń" (usuwanie lokalu / źródła); X5 podaje własną etykietę akcji.
  const askConfirm = (title: string, message: string, onConfirm: () => void, confirmLabel?: string) => {
    setConfirmState({ open: true, title, message, onConfirm, confirmLabel });
  };
  const closeConfirm = () => setConfirmState((s) => ({ ...s, open: false }));

  // Wynik kosza przy lokalu (2026-09-30): odmowa 409 szła do banera na górze widoku — na długiej
  // liście (Bytomska: 30 lokali) niewidoczna z miejsca kliknięcia. Teraz błąd stoi POD wierszem
  // lokalu, a sukces (wiersz znika) jako toast przy dolnej krawędzi — wzorzec ClientsTab.
  const [aptDeleteError, setAptDeleteError] = useState<{ apartmentId: string; text: string } | null>(null);
  const [aptDeleteToast, setAptDeleteToast] = useState<string | null>(null);
  // F8 / B5 reszta (raport uwag Bronka 2026-10-19): superadmin usuwa lokal z POMYŁKOWYM zapisem kontroli
  // (okno z listą tego, co zniknie; o dostępności decyduje serwer). Inni widzą powód blokady jak dotąd.
  const authRole = useAuthOptional()?.user?.role;
  const isSuperadmin = authRole === 'superadmin';
  // Zmiana NUMERÓW lokali (2026-10-21) — tylko admin/superadmin (trasa: requireAdmin).
  const canRenumber = authRole === 'admin' || authRole === 'superadmin';
  const [aptDeleteWithRecordsId, setAptDeleteWithRecordsId] = useState<string | null>(null);
  useEffect(() => {
    if (!aptDeleteToast) return;
    const t = setTimeout(() => setAptDeleteToast(null), 3500);
    return () => clearTimeout(t);
  }, [aptDeleteToast]);

  // Import/odświeżenie danych z CEEB (Z.15). Hook (Z.12) sam zarządza pollingiem 3 s
  // i guardem anty-stale `finishedAt`; tu reagujemy tylko na świeży terminal.
  const { status: importStatus, result: importResult, start: startCeebImport } = useCeebImportPolling();
  const awaitingImportRef = useRef(false);
  const [importMsg, setImportMsg] = useState<{ text: string; severity: 'success' | 'info' | 'error' } | null>(null);
  // Pobieranie podsumowań PDF (per rodzaj — żeby zablokować tylko klikany przycisk).
  const [summaryLoading, setSummaryLoading] = useState<'inaccessible' | 'findings' | null>(null);
  // Karty lokalu (feedback 2026-07-24): zaznaczanie lokali do pobrania + stan ładowania PDF.
  const [selectedCards, setSelectedCards] = useState<Set<string>>(new Set());
  // „Niesprawdzone lokale" (właściciel 2026-10-02, docx) — zestawienie jak „Tylko niezamknięte" na tablecie.
  const [showUnchecked, setShowUnchecked] = useState(false);
  const [cardsLoading, setCardsLoading] = useState(false);
  // „Oznacz nieobecność" dla zaznaczonych lokali (Bronek 2026-10-14) — migawka wyboru z chwili otwarcia okna.
  const [bulkAbsenceFor, setBulkAbsenceFor] = useState<{ id: string; apartmentNumber: string }[] | null>(null);
  const bulkAbsenceSavedRef = useRef(false);
  // E3 (raport uwag Bronka 2026-10-19): „Oznaczenia lokali" — klatka i drzwi obok numeru CEEB. `undefined` = zamknięte,
  // `null` = otwarte bez wskazanego lokalu (przycisk nad tabelą), id = ołówek w wierszu.
  const [labelsFocus, setLabelsFocus] = useState<string | null | undefined>(undefined);
  const [renumberOpen, setRenumberOpen] = useState(false);

  // Skróty i kolejność ze wspólnego słownika (`ds/HeatSourceLabels.ts` — lustro kanonu
  // z mobilki, decyzja właściciela 2026-07-29 „skróty wszędzie"). Wcześniej admin miał
  // tutaj własne pełne etykiety w kolejności CEEB.
  const heatSourceTypes: Array<{ value: HeatSourceType; label: string }> =
    HEAT_SOURCE_TYPE_ORDER.map((value) => ({
      value: value as HeatSourceType,
      label: HEAT_SOURCE_TYPE_LABELS[value],
    }));

  const heatCharacterTypes: Array<{ value: HeatCharacterType; label: string }> = (
    ['heating', 'hot_water', 'heating_and_hot_water'] as HeatCharacterType[]
  ).map((value) => ({ value, label: HEAT_CHARACTER_LABELS[value] }));

  useEffect(() => {
    loadData();
  }, [buildingId, inspectionId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [buildingData, inspectionData, protocolsData, clientsData] = await Promise.all([
        buildingsApi.getById(buildingId),
        // W6: budynek można otworzyć BEZ przeglądu (inspectionId=null) → tryb read-only,
        // nie odpytujemy inspekcji (i nie crashujemy na braku).
        inspectionId ? inspectionsApi.getById(inspectionId) : Promise.resolve(null),
        // F-min (§12–14): historia protokołów CEEB — tolerancyjnie: ŻADEN błąd (w tym
        // synchroniczny brak metody w mockach suit BIV) nie blokuje widoku budynku.
        Promise.resolve()
          .then(() => buildingsApi.getProtocols(buildingId))
          .catch((e) => {
            console.error('Historia protokołów CEEB niedostępna:', e);
            return [] as BuildingProtocolEntry[];
          }),
        // Zarządcy do selecta — tak samo tolerancyjnie (pusta lista ≠ zepsuty widok;
        // select pokaże wtedy tylko „— bez zarządcy —").
        Promise.resolve()
          .then(() => clientsApi.getAll())
          .catch((e) => {
            console.error('Lista zarządców niedostępna:', e);
            return [] as Client[];
          }),
      ]);
      setProtocolHistory(protocolsData || []);
      setZarzadcy(clientsData || []);

      // Check for duplicate apartment numbers
      const apartmentNumbers = buildingData.apartments.map(apt => apt.apartmentNumber);
      const duplicates = apartmentNumbers.filter((num, index) => apartmentNumbers.indexOf(num) !== index);
      if (duplicates.length > 0) {
        console.warn('⚠️ UWAGA: Znaleziono duplikaty numerów lokali:', duplicates);
        console.warn('⚠️ Wszystkie lokale:', buildingData.apartments.map(apt => ({
          id: apt.id,
          number: apt.apartmentNumber
        })));
      }

      setBuilding(buildingData);
      if (inspectionData) {
        // Normalizuj additionalTerms (backend może zwrócić snake_case additional_terms)
        const normalizedInspection = {
          ...inspectionData,
          additionalTerms:
            inspectionData.additionalTerms ?? (inspectionData as any).additional_terms ?? [],
        };
        setInspection(normalizedInspection);

        // Initialize inspection form data
        setInspectionFormData({
          firstTermDate: inspectionData.firstTermDate?.substring(0, 10) || '',
          secondTermDate: inspectionData.secondTermDate?.substring(0, 10) || '',
          notes: inspectionData.notes || '',
          participantName: inspectionData.participantName || '',
        });
      } else {
        // Tryb read-only (W6) — budynek bez przeglądu.
        setInspection(null);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Błąd podczas ładowania danych');
    } finally {
      setLoading(false);
    }
  };

  // Klik „Pobierz/Odśwież z CEEB" — startuje import (hook woła importFromCeeb → poll).
  const handleCeebImport = useCallback(() => {
    if (importStatus === 'running') return;
    setImportMsg(null);
    awaitingImportRef.current = true;
    void startCeebImport(buildingId);
  }, [importStatus, startCeebImport, buildingId]);

  // Reakcja na świeży terminal importu (Z.12). Po done przeładowujemy widok (loadData),
  // bo lokale/metadane/ceeb_id mogły się zmienić; po error pokazujemy komunikat (budynek
  // bez zmian). UWAGA: źródła ciepła z CEEB zapisane RELACYJNIE — w DB, ale niewidoczne
  // w prefill-UI dla świeżego budynku (D8/F1).
  useEffect(() => {
    if (!awaitingImportRef.current) return;
    if (importStatus !== 'done' && importStatus !== 'error') return;
    awaitingImportRef.current = false;

    if (importStatus === 'done') {
      if (importResult?.found) {
        setImportMsg({
          text: `Zaciągnięto z CEEB: ${importResult?.locals ?? 0} lokali / ${importResult?.heatSources ?? 0} źródeł.`,
          severity: 'success',
        });
      } else {
        setImportMsg({
          text: 'Budynek nieznaleziony w CEEB — bez zmian; zarejestruje go bot przy najbliższej synchronizacji.',
          severity: 'info',
        });
      }
      loadData();
    } else {
      setImportMsg({
        text: 'Pobranie danych z CEEB nie powiodło się — spróbuj ponownie później.',
        severity: 'error',
      });
    }
    // loadData jest stabilne w obrębie montażu; celowo poza deps (jak istniejący effect montażu).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importStatus, importResult]);

  const getHeatSourceLabel = (type: HeatSourceType) => {
    return resolveHeatSourceType(type);
  };

  // Lookup NORMALIZUJĄCY: `heat_sources.heat_character` niesie i enumy mobilki, i surowe
  // etykiety CEEB z importu lustra — a admin renderuje przede wszystkim te drugie.
  const getHeatCharacterLabel = (character: HeatCharacterType) => resolveHeatCharacter(character);

  const openOwnerModal = () => {
    setOwnerForm({
      ownerSurname: building?.ownerSurname || '',
      ownerName: building?.ownerName || '',
      ownerEmail: building?.ownerEmail || '',
      clientId: building?.clientId || '',
    });
    setOwnerError(null);
    setShowOwnerModal(true);
  };

  const handleSaveOwnerData = async () => {
    if (!building) return;
    setOwnerSaving(true);
    setOwnerError(null);
    try {
      // Wysyłamy WYŁĄCZNIE trzy pola właściciela — NIE rozlewamy `...building`, bo to
      // wysłałoby też `apartments`, a backend traktuje to jako pełną podmianę lokali.
      const updated = await buildingsApi.update(buildingId, {
        ownerSurname: ownerForm.ownerSurname,
        ownerName: ownerForm.ownerName,
        ownerEmail: ownerForm.ownerEmail,
        // „— bez zarządcy —" → JAWNY null odpina (tri-state w PUT). Pusty string backend ignoruje
        // od 2026-10-02 — tak tablet nie odpina zarządcy zapisem całego budynku.
        clientId: ownerForm.clientId || null,
      });
      setBuilding(updated);
      setShowOwnerModal(false);
    } catch (error) {
      // Błąd MUSI być widoczny dla użytkownika (a nie tylko w konsoli) i modal zostaje
      // otwarty z wpisanymi wartościami — inaczej kominiarz traci to, co wpisał.
      setOwnerError(error instanceof Error ? error.message : 'Nie udało się zapisać danych właściciela.');
    } finally {
      setOwnerSaving(false);
    }
  };

  // Błąd zapisu wspólnych źródeł — baner w sekcji źródeł (409 = zmieniono na tablecie w międzyczasie).
  const [hsError, setHsError] = useState<string | null>(null);
  const handleHeatSourcesError = (error: unknown, fallback: string) => {
    console.error('Błąd zapisu wspólnych źródeł ciepła:', error);
    if (isHeatSourcesConflict(error)) {
      setHsError('Wspólne źródła ciepła zmieniono w międzyczasie na innym urządzeniu (tablet). Wczytano aktualną listę — sprawdź ją i powtórz zmianę.');
      loadData();
    } else {
      setHsError(fallback);
    }
  };

  // X5 (zrzut Pokoju 21, 7.10): szara pigułka „Brak" obok listy źródeł czytała się jak stan. Przełącznik
  // ma pełną etykietę akcji, a gdy są wpisane źródła — pytanie przed zmianą: zaznaczenie „brak" wysyła
  // pustą listę wspólnych źródeł, więc wpisane źródła znikają z budynku (i z protokołu CEEB).
  const requestToggleNoHeatSource = () => {
    if (!building) return;
    if (building.heatSources.length === 0) {
      void handleToggleNoHeatSource();
      return;
    }
    const names = building.heatSources.map((hs) => getHeatSourceLabel(hs.type)).join(', ');
    askConfirm(
      building.noCommonHeatSource ? 'Cofnąć «brak wspólnego źródła»?' : 'Oznaczyć: brak wspólnego źródła?',
      building.noCommonHeatSource
        ? `Budynek przestanie być oznaczony jako „bez wspólnego źródła". Wpisane źródła: ${names}.`
        : `Budynek ma wpisane wspólne źródła: ${names}. Oznaczenie „brak wspólnego źródła" usunie je z budynku `
          + '(i z protokołu CEEB przy następnej wysyłce). Kontynuować?',
      () => {
        closeConfirm();
        void handleToggleNoHeatSource();
      },
      building.noCommonHeatSource ? 'Cofnij «brak»' : 'Oznacz: brak wspólnego źródła',
    );
  };

  const handleToggleNoHeatSource = async () => {
    if (!building) return;
    setHsError(null);
    try {
      // Wysyłamy WYŁĄCZNIE pola dotyczące źródeł ciepła. NIE rozlewamy `...building`,
      // bo to wysłałoby też `apartments` → backend traktuje to jako pełną podmianę lokali.
      // 058: źródła bez zmian jako samo { id } + wersje z odczytu (heatSourcesBase).
      const updatedBuilding = await buildingsApi.update(buildingId, {
        noCommonHeatSource: !building.noCommonHeatSource,
        // 058: wartość „Brak", którą panel widział — 409, gdy tablet przestawił ją w międzyczasie
        noCommonHeatSourceBase: !!building.noCommonHeatSource,
        heatSources: !building.noCommonHeatSource ? [] : untouchedHeatSources(building.heatSources),
        heatSourcesBase: heatSourcesBaseOf(building.heatSources),
      });
      setBuilding(updatedBuilding);
    } catch (error) {
      handleHeatSourcesError(error, 'Błąd podczas aktualizacji budynku — zmiana nie została zapisana.');
    }
  };

  const handleAddHeatSource = async () => {
    // Typy bez „Charakteru” w CEEB (piecokuchnia/koza, trzon): wybór ukryty, wartość techniczna
    // 'heating' — lustro mobilki (BuildingDetailsScreen), właściciel 2026-09-13.
    const bezCharakteru = HEAT_TYPES_WITHOUT_CHARACTER.includes(heatSourceType);
    const charakter = bezCharakteru ? 'heating' : heatCharacter;
    if (!heatSourceType || !charakter || !building) {
      alert('Wybierz typ źródła ciepła i charakter ciepła');
      return;
    }

    setHsError(null);
    try {
      // id klienckie `hs-…` = client_ref (058): ponowienie dodania nie założy drugiego źródła.
      const newHeatSource: HeatSource = {
        id: `hs-${Date.now()}`,
        type: heatSourceType as HeatSourceType,
        heatCharacter: charakter as HeatCharacterType,
      };

      const updatedBuilding = await buildingsApi.update(buildingId, {
        heatSources: [...untouchedHeatSources(building.heatSources), newHeatSource],
        heatSourcesBase: heatSourcesBaseOf(building.heatSources),
      });

      setBuilding(updatedBuilding);
      setShowAddHeatSourceModal(false);
      setHeatSourceType('');
      setHeatCharacter('');
    } catch (error) {
      setShowAddHeatSourceModal(false);
      handleHeatSourcesError(error, 'Błąd podczas dodawania źródła ciepła — źródło nie zostało dodane.');
    }
  };

  const handleRemoveHeatSource = (heatSourceId: string) => {
    if (!building) return;
    askConfirm('Usuń źródło ciepła', 'Czy na pewno chcesz usunąć to źródło ciepła?', async () => {
      closeConfirm();
      setHsError(null);
      try {
        // 058: serwer usunie tylko źródło z bazy i tylko w wersji z odczytu panelu.
        const updatedBuilding = await buildingsApi.update(buildingId, {
          heatSources: untouchedHeatSources(building.heatSources.filter((hs) => hs.id !== heatSourceId)),
          heatSourcesBase: heatSourcesBaseOf(building.heatSources),
        });
        setBuilding(updatedBuilding);
      } catch (error) {
        handleHeatSourcesError(error, 'Błąd podczas usuwania źródła ciepła — źródło nie zostało usunięte.');
      }
    });
  };

  // B8 (2026-10-19): edycja lokalizacji, notatki i parametrów JEDNEGO wspólnego źródła — modal na
  // źródle z odczytu; zapis tą samą ścieżką z kontrolą wersji co tablet (heatSourceParamsPayload).
  const [editSource, setEditSource] = useState<HeatSource | null>(null);
  // Nowy odczyt źródła (np. po 409) → nowy klucz modala → formularz od świeżych wartości.
  const [editFormKey, setEditFormKey] = useState(0);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  // writeId jednej wysyłki: ta sama treść ponawiana po błędzie sieci idzie z tym samym id (058).
  const editWriteRef = useRef<{ body: string; writeId: string } | null>(null);

  const closeEditSource = () => {
    setEditSource(null);
    setEditError(null);
    editWriteRef.current = null;
  };

  const handleOpenEditSource = (hs: HeatSource) => {
    setHsError(null);
    setEditError(null);
    editWriteRef.current = null;
    setEditFormKey((k) => k + 1);
    setEditSource(hs);
  };

  const handleSaveSourceParams = async (changes: Record<string, unknown>) => {
    if (!editSource) return;
    if (Object.keys(changes).length === 0) {
      closeEditSource();
      return;
    }
    const body = JSON.stringify({ id: editSource.id, rev: editSource.rev, changes });
    if (!editWriteRef.current || editWriteRef.current.body !== body) {
      editWriteRef.current = { body, writeId: newWriteId() };
    }
    const payload = heatSourceParamsPayload(editSource, changes, editWriteRef.current.writeId);
    if (!payload) {
      setEditError('Serwer nie podał wersji tego źródła — odśwież widok (Ctrl+Shift+R) i spróbuj ponownie. Zmiany nie zapisano.');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      const updated = await buildingsApi.update(buildingId, payload);
      setBuilding(updated);
      closeEditSource();
    } catch (error) {
      console.error('Błąd zapisu parametrów wspólnego źródła ciepła:', error);
      if (!isHeatSourcesConflict(error)) {
        setEditError('Nie udało się zapisać zmian źródła ciepła — spróbuj ponownie.');
        return;
      }
      // 409: źródło zmieniono w międzyczasie (tablet). Nic nie nadpisujemy — wczytujemy aktualny stan
      // i pokazujemy go w formularzu; admin wprowadza zmianę ponownie na świeżych danych.
      editWriteRef.current = null;
      try {
        const fresh = await buildingsApi.getById(buildingId);
        setBuilding(fresh);
        const freshSource = fresh.noCommonHeatSource ? undefined : fresh.heatSources.find((h) => h.id === editSource.id);
        if (!freshSource) {
          closeEditSource();
          setHsError('To źródło ciepła usunięto w międzyczasie na innym urządzeniu (tablet) albo zaznaczono „Brak" — zmiany nie zapisano. Wczytano aktualną listę.');
          return;
        }
        setEditFormKey((k) => k + 1);
        setEditSource(freshSource);
        setEditError('To źródło zmieniono w międzyczasie na innym urządzeniu (tablet) — Twoich zmian nie zapisano. Wczytano aktualne dane: sprawdź je i wprowadź zmianę ponownie.');
      } catch (reloadError) {
        console.error('Nie wczytano budynku po konflikcie źródeł:', reloadError);
        closeEditSource();
        setHsError('Wspólne źródła ciepła zmieniono w międzyczasie na innym urządzeniu (tablet) — zmian nie zapisano. Odśwież widok, aby zobaczyć aktualne dane.');
      }
    } finally {
      setEditSaving(false);
    }
  };

  const handleAddApartment = async () => {
    if (!newApartmentNumber.trim() || !building) {
      alert('Podaj numer lokalu');
      return;
    }

    const exists = building.apartments.some(
      (apt) => apt.apartmentNumber === newApartmentNumber.trim()
    );

    if (exists) {
      alert('Lokal o tym numerze już istnieje');
      return;
    }

    try {
      const newApartment = {
        id: `apt-${Date.now()}`,
        apartmentNumber: newApartmentNumber.trim(),
        ownerName: '',
        phone: '',
        inspectionStatus: 'not_scheduled' as const,
      };

      // Tylko `apartments` — backend robi diff-upsert po numerze lokalu, więc istniejące
      // lokale (i ich źródła ciepła / rekordy przeglądów) zostają nietknięte.
      const updatedBuilding = await buildingsApi.update(buildingId, {
        apartments: [...building.apartments, newApartment],
      });

      setBuilding(updatedBuilding);
      setShowAddApartmentModal(false);
      setNewApartmentNumber('');
    } catch (error) {
      console.error('Error adding apartment:', error);
      alert('Błąd podczas dodawania lokalu');
    }
  };

  const handleRemoveApartment = (apartmentId: string) => {
    if (!building) return;
    const apt = building.apartments.find((a) => a.id === apartmentId);
    const label = apt ? `lokal ${apt.apartmentNumber}` : 'ten lokal';
    askConfirm('Usuń lokal', `Czy na pewno chcesz usunąć ${label}? Lokal z zapisami kontroli albo istniejący w CEEB nie zostanie usunięty.`, async () => {
      closeConfirm();
      setAptDeleteError(null);
      try {
        // Bronek 2026-09-15: do tej pory szedł tu PUT budynku z listą bez lokalu, a PUT celowo nigdy
        // nie kasuje dzieci (#57) — kosz pytał „czy na pewno” i nic nie robił. Teraz DELETE + odświeżenie.
        await apartmentsApi.delete(apartmentId);
        const fresh = await buildingsApi.getById(buildingId);
        setBuilding(fresh);
        setAptDeleteToast(`Usunięto ${label}.`);
      } catch (error) {
        console.error('Error removing apartment:', error);
        setAptDeleteError({
          apartmentId,
          text: error instanceof Error ? error.message : 'Nie udało się usunąć lokalu',
        });
      }
    });
  };


  const handleViewHistory = async (apartment: any) => {
    try {
      console.log(`🔍 [Historia] ===== POBIERANIE HISTORII DLA LOKALU ${apartment.apartmentNumber} =====`);
      console.log('🔍 [Historia] apartmentId:', apartment.id);
      console.log('🔍 [Historia] apartmentNumber:', apartment.apartmentNumber);

      // Pobierz historię z API
      const history = await apartmentInspectionsApi.getByApartmentId(apartment.id);

      console.log('✅ [Historia] Pobrano rekordów z API:', history.length);
      console.log('✅ [Historia] Rekordy:', history);

      // Sortuj historię od najnowszych do najstarszych
      history.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.inspectionDate).getTime();
        const dateB = new Date(b.createdAt || b.inspectionDate).getTime();
        return dateB - dateA; // Od najnowszych
      });

      setEditingApartment(apartment);
      setInspectionHistory(history);
      setShowHistoryModal(true);
    } catch (error) {
      console.error('❌ [Historia] Błąd podczas ładowania z API:', error);
      alert('Nie udało się załadować historii przeglądów. Sprawdź połączenie z serwerem.');
      setEditingApartment(apartment);
      setInspectionHistory([]);
      setShowHistoryModal(true);
    }
  };

  // Przegląd lokalu jest dostępny ZAWSZE — także w budynku bez wpisanych wspólnych źródeł i bez „Brak" (Bronek
  // 2026-10-21: „Lokal podłączony do wspólnego źródła ciepła budynku" nie wyświetlał się, bo przycisk był wyłączony).
  // Lustro tabletu (M9, BuildingDetailsScreen.handleStartInspection): źródła zbiera się też przy każdym lokalu.
  const handleStartInspection = (apartment: any) => {
    setEditingApartment(apartment);
    setShowInspectionModal(true);
  };

  const handleSaveInspectionEdit = async () => {
    if (!inspection || !inspectionId) return;
    const targetId = inspectionId; // narrowed string — stabilny w domknięciu doUpdate

    // DIRTY-CHECK (W5): wysyłamy TYLKO pola realnie zmienione względem prefillu z inspekcji.
    // Wcześniej PUT zawsze niósł first/second_term_date → cichy re-write terminów aktywnej
    // inspekcji (np. 2027/2028) + bump updated_at nawet gdy admin niczego nie tknął.
    const baseline = {
      firstTermDate: inspection.firstTermDate?.substring(0, 10) || '',
      secondTermDate: inspection.secondTermDate?.substring(0, 10) || '',
      notes: inspection.notes || '',
      participantName: inspection.participantName || '',
    };
    const payload: { firstTermDate?: string; secondTermDate?: string; notes?: string; participantName?: string } = {};
    if (inspectionFormData.firstTermDate !== baseline.firstTermDate) payload.firstTermDate = inspectionFormData.firstTermDate;
    if (inspectionFormData.secondTermDate !== baseline.secondTermDate) payload.secondTermDate = inspectionFormData.secondTermDate;
    if (inspectionFormData.notes !== baseline.notes) payload.notes = inspectionFormData.notes;
    if (inspectionFormData.participantName !== baseline.participantName) payload.participantName = inspectionFormData.participantName;

    // Nic nie zmieniono → nie wołaj update wcale (żaden bump/nadpisanie), tylko wyjdź z edycji.
    if (Object.keys(payload).length === 0) {
      setEditingInspection(false);
      return;
    }

    const termsChanged = 'firstTermDate' in payload || 'secondTermDate' in payload;

    const doUpdate = async (extra?: { rescheduleActive: boolean }) => {
      await inspectionsApi.update(targetId, { ...payload, ...extra });
      // Merge lokalny (backend PUT zwraca snake_case) — trzymamy kształt camelCase w stanie.
      setInspection((prev) => (prev ? { ...prev, ...payload } : prev));
      setEditingInspection(false);
      alert('Dane przeglądu zaktualizowane pomyślnie!');
    };

    try {
      await doUpdate();
    } catch (error) {
      const e = error as InspectionUpdateError;
      // Backend guard (W5): zmiana terminów aktywnej (IN_PROGRESS) inspekcji bez potwierdzenia → 409.
      if (e?.status === 409 && e?.code === 'ACTIVE_INSPECTION_TERMS' && termsChanged) {
        if (window.confirm('Przegląd jest w terenie — na pewno zmienić terminy?')) {
          try {
            await doUpdate({ rescheduleActive: true });
          } catch (retryError) {
            console.error('Error updating inspection (reschedule):', retryError);
            alert(retryError instanceof Error ? retryError.message : 'Błąd podczas aktualizacji danych przeglądu');
          }
        }
        return;
      }
      console.error('Error updating inspection:', error);
      alert('Błąd podczas aktualizacji danych przeglądu');
    }
  };

  // Zapasowa część nazwy pliku, gdy serwer nie poda Content-Disposition (np. cross-origin w dev).
  const fallbackAddressPart = () =>
    asciiFilename(building ? `${building.street}_${building.houseNumber}` : buildingId) || 'budynek';

  // Podsumowania PDF per budynek (właściciel 2026-07-09). Pobieramy Blob przez authedFetch
  // (przy AUTH_ENFORCE=1 goły link dostałby 401) i ZAPISUJEMY pod nazwą z Content-Disposition
  // (audyt 2026-09-30: otwarcie `blob:` w karcie gubiło nazwę `podsumowanie_<ulica>_<nr>_<data>.pdf`).
  // Dodatek do „Podsumowania" (kontrakt #338, właściciel 2026-10-02: wykaz terminów każdego lokalu
  // „mógłby być jako opcja do wydruku") — domyślnie wyłączony, podsumowanie ma mieścić się na stronie.
  const [summaryAppendix, setSummaryAppendix] = useState(false);
  // Źródła ciepła w podsumowaniu (Bronek 14.10) — JEDNA opcja panelu, wspólna z „Pobierz ▾" listy
  // budynków. Domyślnie bez: pobrane podsumowanie i karty to kopie dla zarządcy.
  const [summaryHeatSources] = useSummaryHeatSources();
  const handleDownloadSummary = async (kind: 'inaccessible' | 'findings') => {
    if (summaryLoading) return;
    setSummaryLoading(kind);
    try {
      // Opcje przekazujemy tylko włączone — domyślne żądanie bez zmian (findings.pdf bez query).
      const opts = {
        ...(summaryAppendix ? { appendix: true } : {}),
        ...(summaryHeatSources ? { heatSources: true } : {}),
      };
      const { blob, filename } = kind === 'findings' && Object.keys(opts).length
        ? await buildingsApi.downloadSummary(buildingId, kind, opts)
        : await buildingsApi.downloadSummary(buildingId, kind);
      const prefix = kind === 'findings' ? 'podsumowanie' : 'nieudostepnione';
      saveBlob(blob, filename || `${prefix}_${fallbackAddressPart()}.pdf`);
    } catch (error) {
      console.error('Error downloading summary PDF:', error);
      alert(error instanceof Error ? error.message : 'Nie udało się pobrać podsumowania PDF');
    } finally {
      setSummaryLoading(null);
    }
  };

  // #416 (lista Bronka 12.10, pkt 3): „karty mieszkań z podpisem” — zarządcy chcą podpisy lokatorów.
  // Układ dokumentu, który idzie do CEEB jako załącznik (karty + podsumowanie), generowany na żywo;
  // źródła ciepła w doklejonym podsumowaniu wg tej samej opcji co „PDF: Podsumowanie”.
  const [cardsPdfLoading, setCardsPdfLoading] = useState(false);
  const [cardsPdfError, setCardsPdfError] = useState<string | null>(null);
  const handleDownloadCards = async () => {
    if (cardsPdfLoading || !inspectionId) return;
    setCardsPdfLoading(true);
    setCardsPdfError(null);
    try {
      const { blob, filename } = summaryHeatSources
        ? await inspectionsApi.downloadCards(inspectionId, { heatSources: true })
        : await inspectionsApi.downloadCards(inspectionId);
      saveBlob(blob, filename || `karty_lokali_${fallbackAddressPart()}.pdf`);
    } catch (error) {
      console.error('Error downloading apartment cards PDF:', error);
      setCardsPdfError(error instanceof Error ? error.message : 'Nie udało się pobrać kart lokali');
    } finally {
      setCardsPdfLoading(false);
    }
  };

  // B10 (RAPORT Etap 2 #16): „komplet" także dla JEDNEGO budynku — ten sam job i ten sam
  // mechanizm co „Pobierz ▾ → Pobierz komplet" na liście (useProtocolPdfsPack): ZIP z katalogiem
  // budynku = protokół(y) z CEEB z wybranego roku + podsumowanie kontroli + karty lokali z podpisami.
  const [packYear, setPackYear] = useState(currentProtocolYear);
  const [packMsg, setPackMsg] = useState<ProtocolPackMessage | null>(null);
  const pack = useProtocolPdfsPack(setPackMsg);
  const handleDownloadComplete = async () => {
    setPackMsg(null);
    await pack.start([buildingId], packYear, true);
  };

  // Pobranie PROTOKOŁU kontroli (PDF) — ten sam dokument, który mobilka generuje przy
  // „Generuj PDF". Bez argumentu → wszystkie lokale (jeden PDF); lista numerów → wybrane
  // (multi-select) lub pojedynczy lokal. Backend odtwarza go przez puppeteer (feedback 2026-07-24).
  const handleDownloadProtocols = async (apartmentNumbers?: string[]) => {
    if (cardsLoading) return;
    if (!inspectionId) {
      alert('Brak przeglądu — protokoły wymagają zaplanowanego przeglądu.');
      return;
    }
    setCardsLoading(true);
    try {
      const { blob, filename } = await inspectionsApi.downloadProtocols(inspectionId, apartmentNumbers);
      saveBlob(blob, filename || `protokol_${fallbackAddressPart()}.pdf`);
    } catch (error) {
      console.error('Error downloading protocol PDF:', error);
      alert(error instanceof Error ? error.message : 'Nie udało się pobrać protokołu PDF');
    } finally {
      setCardsLoading(false);
    }
  };

  const toggleCardSelection = (apartmentNumber: string) => {
    setSelectedCards((prev) => {
      const next = new Set(prev);
      if (next.has(apartmentNumber)) next.delete(apartmentNumber);
      else next.add(apartmentNumber);
      return next;
    });
  };

  // Kasowanie po NUMERZE slotu (U6) — działa dla KAŻDEGO terminu, także I i II.
  // Wcześniej szło po indeksie w `additional_terms`, więc terminy ze slotów I/II
  // nie miały jak zniknąć (tworzenie routowało po slocie, kasowanie nie).
  const handleDeleteTerm = (termNumber: number) => {
    if (!inspection || !inspectionId) return;
    const termInspectionId = inspectionId; // narrowed string — stabilny w zagnieżdżonym callbacku askConfirm
    askConfirm(`Usuń ${romanTerm(termNumber) ?? ''} termin`,
      'Czy na pewno chcesz usunąć ten termin?', async () => {
        closeConfirm();
        try {
          const result = await inspectionsApi.deleteTerm(termInspectionId, termNumber);
          setInspection({
            ...inspection,
            firstTermDate: result.terms.find((t) => t.number === 1)?.date ?? undefined,
            secondTermDate: result.terms.find((t) => t.number === 2)?.date ?? undefined,
            additionalTerms: result.additionalTerms,
            terms: result.terms,
            nextTermNumber: result.nextTermNumber,
          });
        } catch (error) {
          console.error('Error deleting term:', error);
          alert(error instanceof Error ? error.message : 'Błąd podczas usuwania terminu');
        }
      });
  };

  // Dodatkowe terminy w kolejności NUMERÓW (zachowujemy oryginalny index dla delete).
  // NUMER bierze się z `inspection.terms` (numeruje backend — patrz InspectionTerm w types.ts),
  // a nie z pozycji na tej liście. Do 2026-07-28 renderowaliśmy `idx + 3`, co zakładało, że
  // I i II termin ZAWSZE są zajęte: budynek z pustym `second_term_date` dostawał wpis opisany
  // jako „3. termin", choć przycisk obiecywał „II termin".
  //
  // ⚠️ 2026-08-05: sortujemy PO NUMERZE, nie po dacie. Numeracja jest SLOTOWA i stabilna
  // (utils/inspectionTerms.js — numer bywa już ostemplowany w attempts[].termNumber), więc
  // termin dopisany wstecz ma wcześniejszą datę przy wyższym numerze. Sortowanie po dacie
  // ustawiało go PRZED terminem o niższym numerze i panel pokazywał „IV: 24.07" nad
  // „III: 31.07", podczas gdy PDF podsumowania (kanon `datedTerms`) drukował je po numerze.
  // Ta sama lista musi wyglądać tak samo w panelu, na tablecie i w dokumencie dla klienta.
  const additionalTermNumbers = new Map<number, number>(
    (inspection?.terms ?? [])
      .filter((t) => t.source === 'additional' && t.additionalIndex != null)
      .map((t) => [t.additionalIndex as number, t.number] as [number, number]),
  );
  const sortedAdditionalTerms = (inspection?.additionalTerms ?? [])
    .map((term, originalIndex) => ({
      term,
      originalIndex,
      number: additionalTermNumbers.get(originalIndex) ?? null,
    }))
    .sort((a, b) => (a.number ?? Number.MAX_SAFE_INTEGER) - (b.number ?? Number.MAX_SAFE_INTEGER));


  // DS: section card wrapper (replaces MUI Paper) — flat surface, hairline border, left status bar
  const sectionStyle = (barColor?: string): React.CSSProperties => ({
    background: 'var(--surface-card)',
    border: 'var(--border-width) solid var(--border)',
    borderLeft: barColor ? `var(--bar-width) solid ${barColor}` : undefined,
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-6)',
    marginBottom: 'var(--space-6)',
  });

  const sectionTitleStyle: React.CSSProperties = {
    margin: 0,
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--text-h2)',
    fontWeight: 'var(--weight-bold)',
    lineHeight: 'var(--leading-tight)',
    color: 'var(--text-heading)',
  };

  if (loading) {
    return (
      <ModalShell open onClose={onClose} hideClose ariaLabel="Ładowanie danych przeglądu" width="md">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
            gap: 'var(--space-4)',
            color: 'var(--text-secondary)',
          }}
        >
          <Icon
            name="refresh"
            size={48}
            color="var(--action-primary)"
            title="Ładowanie"
            style={{ animation: 'bivSpin 1s linear infinite' }}
          />
          <span style={{ fontSize: 'var(--text-body)', fontFamily: 'var(--font-sans)' }}>
            Ładowanie...
          </span>
        </div>
        <style>{`@keyframes bivSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </ModalShell>
    );
  }

  if (!building) {
    return (
      <ModalShell
        open
        onClose={onClose}
        title="Błąd"
        width={480}
        footer={
          <Button variant="primary" onClick={onClose}>
            Zamknij
          </Button>
        }
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-4)',
            background: 'var(--status-deny-bg)',
            border: 'var(--border-width) solid var(--border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--status-deny)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-body)',
          }}
        >
          <Icon name="triangle-alert" size={24} />
          <span>Nie znaleziono danych</span>
        </div>
      </ModalShell>
    );
  }

  const saveSpecialMarking = async () => {
    setMarkingSaving(true);
    setMarkingError(null);
    try {
      // Pusty string = jawne wyczyszczenie (COALESCE pomija tylko null).
      const updated = await buildingsApi.update(buildingId, { specialMarking: markingDraft.trim() });
      setBuilding((prev) => (prev ? { ...prev, specialMarking: updated.specialMarking ?? null } : prev));
      setEditingMarking(false);
    } catch (e) {
      console.error('Error saving special marking:', e);
      setMarkingError('Nie udało się zapisać oznaczenia. Spróbuj ponownie.');
    } finally {
      setMarkingSaving(false);
    }
  };

  const headerTitle = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <span style={{ fontSize: 'var(--text-h2)', fontWeight: 'var(--weight-bold)', color: 'var(--text-heading)', display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        {flueCountsMissing && <FlueWarningIcon testId="biv-flue-warning" size={20} />}
        {building.street} {building.houseNumber}
      </span>
      <span style={{ fontSize: 'var(--text-label)', fontWeight: 'var(--weight-regular)', color: 'var(--text-secondary)' }}>
        {building.postalCode} {building.city}
      </span>
      {/* „Oznaczenie specjalne" (§16, 2026-08-04) — wyświetlanie + edycja inline. */}
      {!editingMarking ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
          {building.specialMarking
            ? <span>{building.specialMarking}</span>
            : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Brak oznaczenia specjalnego</span>}
          <Button
            variant="ghost"
            onClick={() => { setMarkingDraft(building.specialMarking || ''); setMarkingError(null); setEditingMarking(true); }}
          >
            {building.specialMarking ? 'Edytuj' : 'Dodaj'}
          </Button>
        </span>
      ) : (
        <span style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <InputField
            label="Oznaczenie specjalne"
            value={markingDraft}
            onChange={(e) => setMarkingDraft(e.target.value)}
            disabled={markingSaving}
          />
          <Button variant="primary" onClick={saveSpecialMarking} disabled={markingSaving}>
            {markingSaving ? 'Zapisywanie…' : 'Zapisz'}
          </Button>
          <Button variant="secondary" onClick={() => setEditingMarking(false)} disabled={markingSaving}>
            Anuluj
          </Button>
          {markingError && (
            <span style={{ fontSize: 'var(--text-caption)', color: 'var(--status-deny)' }}>{markingError}</span>
          )}
        </span>
      )}
      {building.ceebId && (
        <span
          style={{
            display: 'inline-flex',
            alignSelf: 'flex-start',
            marginTop: 'var(--space-1)',
            padding: '4px 12px',
            background: 'var(--action-primary)',
            color: 'var(--text-on-action)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-caption)',
            fontWeight: 'var(--weight-semibold)',
          }}
        >
          {/* 2026-10-15 (Bronek): sprawa z poprzedniego roku = OSTATNI protokół w CEEB, nie ten, nad którym pracujemy. */}
          {ceebProtocolLabel(building.ceebId)}
        </span>
      )}
    </div>
  );

  return (
    <ModalShell open onClose={onClose} title={headerTitle} width="min(90vw, 1000px)">
      {/* Terminy */}
      <div style={sectionStyle('var(--action-primary)')}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-3)', marginBottom: 'var(--space-5)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <Icon name="calendar" size={26} color="var(--action-primary)" />
            <h3 style={sectionTitleStyle}>Terminy przeglądów</h3>
          </div>
          {/* Rząd akcji zawija się (flexWrap) — 4 przyciski (Pobierz/Odśwież z CEEB + 2× PDF +
              Edytuj) nie mieszczą się w jednej linii w modalu min(90vw,1000px) i przycinały
              się poza prawą krawędź (regresja po dodaniu przycisków PDF, 2026-07-09). */}
          {!editingInspection ? (
            <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button
                variant="secondary"
                icon="refresh"
                onClick={handleCeebImport}
                disabled={importStatus === 'running'}
              >
                {importStatus === 'running'
                  ? 'Pobieranie z CEEB…'
                  : building.ceebId
                  ? 'Odśwież z CEEB'
                  : 'Pobierz z CEEB'}
              </Button>
              {/* Podsumowania PDF budynku (właściciel 2026-07-09) — 2 dokumenty dla zarządcy:
                  nieudostępnione (brak dostępu + odmowy) i JEDNO podsumowanie (pkt 12/15,
                  2026-09-13: dane budynku → nieprawidłowości budynku → lokale → zalecenia per lokal). */}
              <Button
                variant="secondary"
                icon="clipboard"
                onClick={() => handleDownloadSummary('inaccessible')}
                disabled={summaryLoading !== null}
              >
                {/* „PDF: Nieudostępnione" czytało się jak komunikat o błędzie („PDF niedostępny"),
                    a to nazwa dokumentu — lista lokali bez dostępu (QA 2026-08-05, D14). */}
                {summaryLoading === 'inaccessible' ? 'Generowanie…' : 'PDF: Lokale nieudostępnione'}
              </Button>
              <Button
                variant="secondary"
                icon="clipboard"
                onClick={() => handleDownloadSummary('findings')}
                disabled={summaryLoading !== null}
              >
                {summaryLoading === 'findings' ? 'Generowanie…' : 'PDF: Podsumowanie'}
              </Button>
              {inspectionId && (
                <Button
                  variant="secondary"
                  icon="download"
                  data-testid="download-apartment-cards"
                  onClick={handleDownloadCards}
                  disabled={cardsPdfLoading}
                  title="Karty lokali z podpisami lokatorów i podsumowaniem — układ załącznika CEEB; źródła ciepła wg opcji „Dołącz informacje o źródłach ciepła”"
                >
                  {cardsPdfLoading ? 'Generowanie…' : 'PDF: Karty lokali (z podpisami)'}
                </Button>
              )}
              <label
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)',
                  fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', cursor: 'pointer',
                }}
                title="Dotyczy „PDF: Podsumowanie”: dodatkowa strona z wykazem lokali, ich statusem końcowym i przebiegiem kontroli"
              >
                <input
                  type="checkbox"
                  checked={summaryAppendix}
                  disabled={summaryLoading !== null}
                  onChange={(e) => setSummaryAppendix(e.target.checked)}
                />
                Dołącz stronę: status i przebieg kontroli w lokalach
              </label>
              {/* Bronek 14.10: dotyczy „PDF: Podsumowanie” i „PDF: Karty lokali” — wspólna opcja panelu. */}
              <SummaryHeatSourcesOption disabled={summaryLoading !== null || cardsPdfLoading} />
              {cardsPdfError && (
                <span role="alert" style={{ flexBasis: '100%', fontSize: 'var(--text-caption)', color: 'var(--status-deny)' }}>
                  {cardsPdfError}
                </span>
              )}
              {/* B10: komplet jednego budynku — ten sam job co „Pobierz komplet" na liście. */}
              <div
                data-testid="building-complete-download"
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap', flexBasis: '100%', justifyContent: 'flex-end' }}
              >
                <Button
                  variant="secondary"
                  icon="download"
                  data-testid="download-building-complete"
                  onClick={handleDownloadComplete}
                  disabled={pack.job.status === 'running'}
                  title={`Jeden ZIP: protokół z CEEB${packYear ? ` z roku ${packYear}` : ' (wszystkie lata)'}, podsumowanie kontroli i karty lokali z podpisami lokatorów — to samo co „Pobierz komplet” na liście budynków`}
                >
                  {pack.job.status === 'running'
                    ? 'Przygotowywanie paczki…'
                    : 'Pobierz komplet (protokół CEEB + podsumowanie + karty)'}
                </Button>
                <ProtocolYearSelect value={packYear} onChange={setPackYear} disabled={pack.job.status === 'running'} />
                <span style={{ flexBasis: '100%', textAlign: 'right', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
                  Protokół CEEB pobiera serwer, logując się do CEEB (tak jak na liście budynków) — może to potrwać kilka minut.
                  Podsumowanie bez źródeł ciepła, chyba że zaznaczysz „Dołącz informacje o źródłach ciepła”.
                </span>
                {packMsg && (
                  <span
                    role={packMsg.kind === 'error' ? 'alert' : 'status'}
                    data-testid="building-complete-message"
                    style={{
                      flexBasis: '100%', textAlign: 'right', fontSize: 'var(--text-caption)',
                      color: packMsg.kind === 'error' ? 'var(--status-deny)' : 'var(--status-rec)',
                    }}
                  >
                    {packMsg.text}
                  </span>
                )}
              </div>
              {/* Edycja terminów tylko gdy jest przegląd (W6 — read-only bez inspekcji). */}
              {inspection && (
                <Button variant="secondary" icon="pencil" onClick={() => setEditingInspection(true)}>
                  Edytuj
                </Button>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button
                variant="secondary"
                icon="x"
                onClick={() => {
                  setEditingInspection(false);
                  setInspectionFormData({
                    firstTermDate: inspection?.firstTermDate?.substring(0, 10) || '',
                    secondTermDate: inspection?.secondTermDate?.substring(0, 10) || '',
                    notes: inspection?.notes || '',
                    participantName: inspection?.participantName || '',
                  });
                }}
              >
                Anuluj
              </Button>
              <Button variant="primary" icon="check" onClick={handleSaveInspectionEdit}
                disabled={!!(termDateError(inspectionFormData.firstTermDate) || termDateError(inspectionFormData.secondTermDate))}>
                Zapisz
              </Button>
            </div>
          )}
        </div>

        {/* Komunikat / spinner importu CEEB (Z.15) */}
        {(importStatus === 'running' || importMsg) && (
          <div
            role={importMsg?.severity === 'error' ? 'alert' : 'status'}
            aria-live="polite"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              marginBottom: 'var(--space-5)',
              padding: 'var(--space-3) var(--space-4)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-label)',
              background:
                importMsg?.severity === 'success'
                  ? 'var(--status-ok-bg)'
                  : importMsg?.severity === 'error'
                  ? 'var(--status-deny-bg)'
                  : 'var(--changed-bg)',
              color:
                importMsg?.severity === 'success'
                  ? 'var(--status-ok)'
                  : importMsg?.severity === 'error'
                  ? 'var(--status-deny)'
                  : 'var(--changed-text)',
              borderLeft: `var(--bar-width) solid ${
                importMsg?.severity === 'success'
                  ? 'var(--status-ok-bar)'
                  : importMsg?.severity === 'error'
                  ? 'var(--status-deny-bar)'
                  : 'var(--action-primary)'
              }`,
            }}
          >
            {importStatus === 'running' ? (
              <span style={{ display: 'inline-flex', animation: 'bivSpin 1s linear infinite' }}>
                <style>{`@keyframes bivSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                <Icon name="refresh" size={18} />
              </span>
            ) : (
              <Icon
                name={importMsg?.severity === 'error' ? 'triangle-alert' : 'check-circle'}
                size={18}
              />
            )}
            <span style={{ flex: 1 }}>
              {importStatus === 'running'
                ? 'Pobieranie danych z CEEB… to potrwa około minuty.'
                : importMsg?.text}
            </span>
          </div>
        )}

        {/* Zadanie 5.1 + 5.6 (2026-08-20): przycisk „Zarejestruj w CEEB" ZNIKA — rejestracja
            przestała być osobną czynnością operatora i jest krokiem 1–2 FAZY A, która rusza
            automatycznie przy dodaniu budynku. Tu pokazujemy jej postęp (5 kroków) i — gdy
            padnie — komunikat „na czym stanęło" z przyciskiem „Ponów". */}
        <CeebPhaseAPanel
          buildingId={buildingId}
          caseNumber={building.ceebId}
          onFinished={loadData}
        />

        {/* Raport ostatniego przebiegu bota CEEB (#51b) — widoczny, gdy dotyczy TEGO
            przeglądu (np. po „Wyślij do CEEB" z widoku Budynki/PipelineView). */}
        {/* E2 Z4: tablet trzyma nowszą wersję lokali — wystawienie w CEEB czeka na jego wysyłkę. */}
        <TabletHeldBanner buildingId={buildingId} />

        {inspectionId && <CeebLastRunReport inspectionId={inspectionId} />}

        {/* 074 (właściciel 2026-10-21): protokół zniknął z CEEB — znacznik z numerem i datą wykrycia (ta sama reguła
            co lista budynków, `deletedMarker` z serwera). Bez akcji „przywróć” — tego się nie da. */}
        {protocolHistory.filter((p) => p.deletedMarker).slice(0, 1).map((p) => (
          <div
            key={`deleted-${p.ceebId}`}
            role="status"
            data-testid="ceeb-protocol-deleted-banner"
            style={{
              display: 'flex', gap: 'var(--space-3)', alignItems: 'center', marginBottom: 'var(--space-5)',
              padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-sm)',
              background: 'var(--status-deny-bg)', borderLeft: '3px solid var(--status-deny-bar)', color: 'var(--status-deny)',
              fontSize: 'var(--text-body)',
            }}
          >
            <Icon name="triangle-alert" size={18} />
            <span>{ceebDeletedFull(p.ceebId, p.deletedInCeebAt)}</span>
          </div>
        ))}

        {/* RAPORT G8: protokół usunięty w CEEB — superadmin odpina numer sprawy (serwer decyduje, czy wolno). */}
        <CeebUnlinkCasePanel buildingId={buildingId} onUnlinked={loadData} />

        {/* F-min (§12–14, decyzja 2026-08-04): historia protokołów CEEB budynku —
            rok → numer sprawy → status. Puste = brak historii (import jej nie znalazł). */}
        {protocolHistory.length > 0 && (
          <div style={sectionStyle('var(--text-secondary)')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
              <Icon name="clipboard" size={24} color="var(--text-secondary)" />
              <h3 style={sectionTitleStyle}>Historia protokołów CEEB ({protocolHistory.length})</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {protocolHistory.map((p) => (
                <div
                  key={p.ceebId}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap',
                    padding: 'var(--space-3) var(--space-4)',
                    background: p.isCurrent ? 'var(--surface-fill)' : 'var(--surface-card)',
                    border: 'var(--border-width) solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--text-body)',
                  }}
                >
                  <span style={{ fontWeight: 'var(--weight-semibold)', minWidth: 56 }}>{p.year ?? '—'}</span>
                  <span style={{ color: 'var(--text-secondary)', minWidth: 120 }}>{p.ceebId}</span>
                  <span style={{ color: 'var(--text-secondary)' }}>{p.ceebStatus || 'status nieznany'}</span>
                  {/* 074: wiersz usuniętego protokołu zostaje jako historia — z datą wykrycia. */}
                  {p.deletedInCeebAt && (
                    <span data-testid={`ceeb-protocol-history-deleted-${p.ceebId}`} style={{ color: 'var(--status-deny)', fontSize: 'var(--text-caption)' }}>
                      usunięty w CEEB{deletedDetectedLabel(p.deletedInCeebAt) ? ` (wykryto ${deletedDetectedLabel(p.deletedInCeebAt)})` : ''}
                    </span>
                  )}
                  {/* „bieżący" mylił (Bronek 2026-10-15): to najnowszy protokół ZNALEZIONY w CEEB — gdy jest
                      z poprzedniego roku, protokół bieżącego roku powstanie dopiero przy „Wyślij do CEEB". */}
                  {p.isCurrent && (
                    <span style={{ marginLeft: 'auto', fontSize: 'var(--text-caption)', padding: '2px 10px', borderRadius: 'var(--radius-pill)', background: 'var(--action-primary)', color: 'var(--text-on-action)' }}>
                      najnowszy w CEEB
                      {isBeforeCurrentYear(p.year ?? ceebCaseYear(p.ceebId)) &&
                        ` (protokół ${new Date().getFullYear()} jeszcze nie założony)`}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* W6 — read-only: budynek bez przeglądu. Hint + (opcjonalnie) skok do „Zaplanuj". */}
        {!inspection && (
          <div
            style={{
              padding: 'var(--space-5)',
              background: 'var(--surface-fill)',
              border: 'var(--border-width) dashed var(--border-strong)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
              alignItems: 'flex-start',
            }}
          >
            <div style={{ fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>
              Budynek bez zaplanowanego przeglądu.
            </div>
            {onPlanInspection && (
              <Button variant="primary" icon="calendar" onClick={onPlanInspection}>
                Zaplanuj przegląd
              </Button>
            )}
          </div>
        )}

        {inspection && (!editingInspection ? (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: 'var(--space-4)',
              }}
            >
              <div
                style={{
                  background: 'var(--surface-card)',
                  border: 'var(--border-width) solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-4)',
                }}
              >
                <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>I termin</div>
                <div
                  style={{
                    marginTop: 'var(--space-1)',
                    fontSize: 'var(--text-h2)',
                    fontWeight: 'var(--weight-semibold)',
                    color: 'var(--text-primary)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatDate(inspection.firstTermDate)}
                </div>
              </div>
              <div
                style={{
                  background: 'var(--surface-card)',
                  border: 'var(--border-width) solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-4)',
                }}
              >
                <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>II termin</div>
                <div
                  style={{
                    marginTop: 'var(--space-1)',
                    fontSize: 'var(--text-h2)',
                    fontWeight: 'var(--weight-semibold)',
                    color: 'var(--text-primary)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatDate(inspection.secondTermDate)}
                </div>
              </div>
            </div>
            {/* Dopisek kominiarza do zaleceń BUDYNKU (migr. 040, issue #162) — READ-ONLY.
                Writerem jest mobilka (kominiarz w terenie); admin tylko pokazuje, co pojedzie
                do pola „Wskazane nieprawidłowości" w protokole CEEB. */}
            {inspection.recommendationsNote && (
              <div
                style={{
                  marginTop: 'var(--space-4)',
                  padding: 'var(--space-4)',
                  background: 'var(--surface-fill)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <div style={{ fontSize: 'var(--text-label)', fontWeight: 'var(--weight-semibold)', marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                  Uwagi do zaleceń budynku:
                </div>
                <div style={{ fontSize: 'var(--text-body)', whiteSpace: 'pre-wrap', color: 'var(--text-primary)' }}>
                  {inspection.recommendationsNote}
                </div>
                <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>
                  Wpisane w aplikacji mobilnej — trafia do protokołu CEEB
                </div>
              </div>
            )}
            {/* 076 (zadanie 4, 2026-10-21): dopisek do „Inne uchybienia…" — pisze go wyłącznie panel. */}
            {inspectionId && (
              <OtherDeficienciesNoteSection
                inspectionId={inspectionId}
                value={inspection.otherDeficienciesNote || ''}
                onChange={(next) => setInspection((prev) => (prev ? { ...prev, otherDeficienciesNote: next } : prev))}
              />
            )}
            {/* 079 (zadanie 4 etap 2): własny PDF kart mieszkań zamiast naszych kart — sekcja sama znika bez flagi serwera. */}
            {inspectionId && <UserCardsAttachmentSection inspectionId={inspectionId} />}
            {inspection.notes && (
              <div
                style={{
                  marginTop: 'var(--space-4)',
                  padding: 'var(--space-4)',
                  background: 'var(--surface-fill)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <div style={{ fontSize: 'var(--text-label)', fontWeight: 'var(--weight-semibold)', marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                  Notatki:
                </div>
                <div style={{ fontSize: 'var(--text-body)', whiteSpace: 'pre-wrap', color: 'var(--text-primary)' }}>
                  {inspection.notes}
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', marginTop: 'var(--space-4)' }}>
            {/* Usuwanie I/II (U6): pole daty samo ich nie wyczyści — PUT używa COALESCE
                („null = zachowaj", data-safety #57), więc jedyną drogą jest DELETE po
                numerze slotu. Przycisk pojawia się tylko wtedy, gdy termin realnie istnieje;
                backend i tak odrzuci 409, jeśli są w nim zapisane wizyty. */}
            <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <InputField
                  label="I termin"
                  type="date"
                  min={termDateBounds().min}
                  max={termDateBounds().max}
                  value={inspectionFormData.firstTermDate}
                  errorMessage={termDateError(inspectionFormData.firstTermDate) || undefined}
                  onChange={(e) => setInspectionFormData({ ...inspectionFormData, firstTermDate: e.target.value })}
                />
              </div>
              {inspection?.terms?.some((t) => t.number === 1) && (
                <Button variant="secondary" icon="trash" aria-label="Usuń I termin"
                  onClick={() => handleDeleteTerm(1)}>Usuń</Button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <InputField
                  label="II termin"
                  type="date"
                  min={termDateBounds().min}
                  max={termDateBounds().max}
                  value={inspectionFormData.secondTermDate}
                  errorMessage={termDateError(inspectionFormData.secondTermDate) || undefined}
                  onChange={(e) => setInspectionFormData({ ...inspectionFormData, secondTermDate: e.target.value })}
                />
              </div>
              {inspection?.terms?.some((t) => t.number === 2) && (
                <Button variant="secondary" icon="trash" aria-label="Usuń II termin"
                  onClick={() => handleDeleteTerm(2)}>Usuń</Button>
              )}
            </div>
            {/* 044 (§1 fala 2) + 060: 3. str. protokołu CEEB; puste = osoba z ustawień firmy (migawka przeglądu). */}
            <InputField
              label="Osoba uczestnicząca w kontroli (puste = osoba z ustawień firmy)"
              value={inspectionFormData.participantName}
              onChange={(e) => setInspectionFormData({ ...inspectionFormData, participantName: e.target.value })}
              placeholder="np. Jan Kowalski (właściciel)"
            />
            <InputField
              label="Notatki"
              multiline
              rows={4}
              value={inspectionFormData.notes}
              onChange={(e) => setInspectionFormData({ ...inspectionFormData, notes: e.target.value })}
              placeholder="Dodaj notatki..."
            />
          </div>
        ))}

        {/* Dodatkowe terminy przy BUDYNKU (III, IV…) — pod 2 bazowymi terminami.
            Tylko gdy jest przegląd (W6 — read-only budynek nie ma terminów) i gdy JAKIEŚ są:
            właściciel 2026-10-02 (docx) — „jeśli nie ma dodatkowych, to nie pokazywać w widoku".
            Dodawanie III+ terminu z admina usunięte („Zaplanuj III termin — niepotrzebny"); I i II
            termin edytuje się polami wyżej. Istniejące dodatkowe terminy zostają widoczne i można je
            usunąć w trybie edycji (np. dopisane z tabletu). */}
        {inspection && sortedAdditionalTerms.length > 0 && (
        <div style={{ marginTop: 'var(--space-5)' }}>
          <div
            style={{
              fontSize: 'var(--text-label)',
              fontWeight: 'var(--weight-semibold)',
              color: 'var(--text-secondary)',
              marginBottom: 'var(--space-3)',
            }}
          >
            Dodatkowe terminy ({sortedAdditionalTerms.length})
          </div>

            <div
              style={{
                border: 'var(--border-width) solid var(--border)',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
              }}
            >
              {sortedAdditionalTerms.map(({ term, originalIndex, number }, idx) => (
                <div
                  key={originalIndex}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'var(--space-4)',
                    padding: 'var(--space-4) var(--space-5)',
                    borderBottom:
                      idx < sortedAdditionalTerms.length - 1
                        ? 'var(--border-width) solid var(--border)'
                        : 'none',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 'var(--text-body)',
                        fontWeight: 'var(--weight-semibold)',
                        color: 'var(--text-primary)',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {/* Numer z backendu (inspection.terms) — NIE pozycja na liście. */}
                      {romanTerm(number) ?? '—'} termin: {formatDate(term.date)}
                    </div>
                    {term.reason && (
                      <div style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', marginTop: 4 }}>
                        {term.reason}
                      </div>
                    )}
                    {term.addedAt && (
                      <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: 4 }}>
                        Dodano: {formatDate(term.addedAt)}
                      </div>
                    )}
                  </div>
                  {editingInspection && (
                    <button
                      type="button"
                      title="Usuń dodatkowy termin"
                      aria-label="Usuń dodatkowy termin"
                      onClick={() => handleDeleteTerm(number ?? 0)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 44,
                        height: 44,
                        flex: 'none',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--status-deny)',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--status-deny-bg)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <Icon name="trash" size={22} />
                    </button>
                  )}
                </div>
              ))}
            </div>
        </div>
        )}
      </div>

      {/* Dane właściciela / zarządcy (035, issue #167) — do 2026-07-29 edytowalne tylko z tabletu. */}
      <div style={sectionStyle('var(--border-color)')}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            {/* Ta sama ikona co w mobilnej sekcji „Dane właściciela / zarządcy" — zestaw DS
                nie ma „user", a mobilka używa tu „building". */}
            <Icon name="building" size={26} color="var(--text-secondary)" />
            <h3 style={sectionTitleStyle}>Dane właściciela / zarządcy</h3>
          </div>
          {/* aria-label jednoznaczny: na tym ekranie jest już drugi przycisk „Edytuj"
              (przegląd), a samo „Edytuj" nie mówi czytnikowi ekranu, czego dotyczy. */}
          <Button
            variant="secondary"
            onClick={openOwnerModal}
            aria-label={
              building.ownerSurname || building.ownerName || building.ownerEmail
                ? 'Edytuj dane właściciela'
                : 'Uzupełnij dane właściciela'
            }
          >
            {building.ownerSurname || building.ownerName || building.ownerEmail ? 'Edytuj' : 'Uzupełnij'}
          </Button>
        </div>
        <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>
          Trafiają na 3. stronę protokołu CEEB.
        </div>
        {building.ownerSurname || building.ownerName || building.ownerEmail || building.clientId ? (
          <div style={{ display: 'grid', gap: 'var(--space-2)', fontSize: 'var(--text-body)', color: 'var(--text-primary)' }}>
            <div>
              <strong>Zarządca:</strong>{' '}
              {building.clientId
                ? (zarzadcy.find((c) => c.id === building.clientId)?.name || building.clientId)
                : '—'}
            </div>
            <div><strong>Nazwisko / nazwa firmy:</strong> {building.ownerSurname || '—'}</div>
            {/* B2 (właściciel 2026-10-02, docx: „w widoku usunąć wiersz Imię/nazwa skrócona"; raport uwag
                2026-10-20): wiersza nie ma w WIDOKU nigdy. Pole zostaje w oknie edycji („Edytuj"), bo bot
                wpisuje je w CEEB jako imię właściciela (owner_name, 3. strona protokołu). */}
            <div><strong>E-mail:</strong> {building.ownerEmail || '—'}</div>
          </div>
        ) : (
          <div style={{ fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>
            Brak danych — bot zostawi te pola w CEEB bez zmian (carry-over).
          </div>
        )}
      </div>

      {/* Źródła ciepła */}
      <div style={sectionStyle('var(--status-rec-bar)')}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-5)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <Icon name="flame" size={26} color="var(--status-rec)" />
            <h3 style={sectionTitleStyle}>Wspólne źródła ciepła</h3>
          </div>
          <Button variant="secondary" onClick={requestToggleNoHeatSource} data-testid="toggle-no-common-heat">
            {building.noCommonHeatSource ? 'Cofnij «brak»' : 'Oznacz: brak wspólnego źródła'}
          </Button>
        </div>

        {/* X5: stan wprost (a nie wygląd przełącznika). */}
        <div
          data-testid="common-heat-state"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginTop: 'calc(-1 * var(--space-3))',
            marginBottom: 'var(--space-4)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-body)',
            color: building.noCommonHeatSource || building.heatSources.length > 0 ? 'var(--text-primary)' : 'var(--text-secondary)',
          }}
        >
          {building.noCommonHeatSource ? (
            <>
              <Icon name="check" size={20} color="var(--status-ok)" />
              <span>Budynek nie ma wspólnego źródła</span>
            </>
          ) : building.heatSources.length > 0 ? (
            <span>
              <strong>Wspólne źródła:</strong>{' '}
              {building.heatSources.map((hs) => getHeatSourceLabel(hs.type)).join(', ')}
            </span>
          ) : (
            <span>Nie wpisano wspólnych źródeł — dodaj je albo oznacz „brak wspólnego źródła".</span>
          )}
        </div>

        {hsError && (
          <div
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              marginBottom: 'var(--space-4)',
              padding: 'var(--space-3) var(--space-4)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-label)',
              background: 'var(--status-deny-bg)',
              color: 'var(--status-deny)',
            }}
          >
            <Icon name="triangle-alert" size={18} />
            <span style={{ flex: 1 }}>{hsError}</span>
            <Button variant="ghost" onClick={() => setHsError(null)}>
              Zamknij
            </Button>
          </div>
        )}

        {!building.noCommonHeatSource && (
          <>
            {building.heatSources.length > 0 && (
              <div
                style={{
                  border: 'var(--border-width) solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--space-4)',
                  overflow: 'hidden',
                }}
              >
                {building.heatSources.map((hs, index) => (
                  <div
                    key={hs.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 'var(--space-4)',
                      padding: 'var(--space-4) var(--space-5)',
                      borderBottom:
                        index < building.heatSources.length - 1
                          ? 'var(--border-width) solid var(--border)'
                          : 'none',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--text-body)', fontWeight: 'var(--weight-medium)', color: 'var(--text-primary)' }}>
                        {index + 1}. {getHeatSourceLabel(hs.type)}
                      </div>
                      {!HEAT_TYPES_WITHOUT_CHARACTER.includes(hs.type) && (
                        <div style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', marginTop: 4 }}>
                          {getHeatCharacterLabel(hs.heatCharacter)}
                        </div>
                      )}
                      {/* Prompt 07 pkt 1: lokalizacja, notatka i parametry z tabletu; edycja — ołówek (B8). */}
                      <HeatSourceDetails type={hs.type} params={hs.params} />
                    </div>
                    <button
                      type="button"
                      title="Edytuj źródło ciepła"
                      aria-label={`Edytuj źródło ciepła: ${index + 1}. ${getHeatSourceLabel(hs.type)}`}
                      onClick={() => handleOpenEditSource(hs)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 44,
                        height: 44,
                        flex: 'none',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--text-secondary)',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-fill)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <Icon name="pencil" size={22} />
                    </button>
                    <button
                      type="button"
                      title="Usuń źródło ciepła"
                      aria-label="Usuń źródło ciepła"
                      onClick={() => handleRemoveHeatSource(hs.id)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 44,
                        height: 44,
                        flex: 'none',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--status-deny)',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--status-deny-bg)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <Icon name="trash" size={22} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Button variant="secondary" icon="plus" fullWidth onClick={() => setShowAddHeatSourceModal(true)}>
              Dodaj źródło ciepła
            </Button>
          </>
        )}
      </div>

      {/* Pkt 11 (właściciel 2026-09-13): zestawienie INDYWIDUALNYCH źródeł ciepła lokali — typ → sztuk.
          Wspólne źródła budynku są wyżej i tu nie wchodzą. Etykiety ze słownika (HeatSourceLabels). */}
      {building.heatSourceTotals && (
        <div style={sectionStyle('var(--status-rec-bar)')} data-testid="heat-source-totals">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
            <Icon name="flame" size={26} color="var(--status-rec)" />
            <h3 style={sectionTitleStyle}>Źródła ciepła w lokalach ({building.heatSourceTotals.total})</h3>
          </div>
          {building.heatSourceTotals.byType.length === 0 ? (
            <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--text-label)' }}>
              Brak zaewidencjonowanych indywidualnych źródeł ciepła w lokalach.
            </p>
          ) : (
            <table style={{ borderCollapse: 'collapse', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-label)', marginBottom: 'var(--space-3)' }}>
              <tbody>
                {building.heatSourceTotals.byType.map((t) => (
                  <tr key={t.type}>
                    <td style={{ padding: '4px 24px 4px 0', color: 'var(--text-heading)' }}>{heatSourceColumnLabel(t.type)}</td>
                    <td style={{ padding: '4px 0', fontWeight: 'var(--weight-bold)', color: 'var(--text-heading)', textAlign: 'right' }}>{t.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--text-caption)' }}>
            Lokale z własnymi źródłami: {building.heatSourceTotals.apartmentsWithOwn}
            {' · '}podłączone do wspólnego źródła: {building.heatSourceTotals.apartmentsOnCommon}
            {' · '}bez źródła: {building.heatSourceTotals.apartmentsNoSource}
            {' · '}bez danych: {building.heatSourceTotals.apartmentsUnknown}
          </p>
        </div>
      )}

      {/* Lokale */}
      <div style={{ marginTop: 'var(--space-6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-5)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <Icon name="building" size={26} color="var(--action-primary)" />
            <h3 style={sectionTitleStyle}>Lokale ({building.apartments.length})</h3>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {/* Protokoły: pobierz zaznaczone (multi-select) → jeden PDF, albo wszystkie gdy nic
                nie zaznaczono. Wymaga przeglądu (inspectionId). Feedback właściciela 2026-07-24. */}
            {inspectionId && (
              <Button
                variant="secondary"
                icon="clipboard"
                disabled={cardsLoading}
                onClick={() => handleDownloadProtocols(selectedCards.size ? Array.from(selectedCards) : undefined)}
              >
                {cardsLoading
                  ? 'Pobieranie…'
                  : selectedCards.size
                    ? `Pobierz protokoły (${selectedCards.size})`
                    : 'Pobierz protokoły (wszystkie)'}
              </Button>
            )}
            {/* Zbiorcza nieobecność z kartki (Bronek 2026-10-14) — dla zaznaczonych lokali, tylko przy przeglądzie. */}
            {inspection && inspectionId && selectedCards.size > 0 && (
              <Button
                variant="secondary"
                icon="calendar"
                onClick={() => setBulkAbsenceFor(
                  building.apartments
                    .filter((a) => selectedCards.has(a.apartmentNumber))
                    .map((a) => ({ id: a.id, apartmentNumber: a.apartmentNumber })),
                )}
              >
                {`Oznacz nieobecność (${selectedCards.size})`}
              </Button>
            )}
            {building.apartments.length > 0 && (
              <Button variant="secondary" icon="circle-dot" onClick={() => setShowUnchecked(true)}>
                Niesprawdzone lokale
              </Button>
            )}
            {building.apartments.length > 0 && (
              <Button variant="secondary" icon="pencil" onClick={() => setLabelsFocus(null)}>
                Oznaczenia lokali
              </Button>
            )}
            {canRenumber && building.apartments.length > 0 && (
              <Button variant="secondary" icon="pencil" onClick={() => setRenumberOpen(true)}>
                Zmień numery lokali
              </Button>
            )}
            <Button variant="secondary" icon="plus" onClick={() => setShowAddApartmentModal(true)}>
              Dodaj lokal
            </Button>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            border: 'var(--border-width) solid var(--border)',
            borderRadius: 'var(--radius-md)',
            overflowX: 'auto',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)' }}>
            <thead>
              <tr style={{ background: 'var(--surface-fill)' }}>
                {inspectionId && (
                  <th
                    style={{
                      width: 40,
                      textAlign: 'center',
                      padding: 'var(--space-3) var(--space-2)',
                      borderBottom: 'var(--border-width) solid var(--border)',
                    }}
                    title="Zaznacz lokale (pobranie protokołów, zbiorcza nieobecność)"
                  >
                    <input
                      type="checkbox"
                      aria-label="Zaznacz wszystkie lokale"
                      checked={building.apartments.length > 0 && selectedCards.size === building.apartments.length}
                      onChange={(e) =>
                        setSelectedCards(
                          e.target.checked
                            ? new Set(building.apartments.map((a) => a.apartmentNumber))
                            : new Set(),
                        )
                      }
                    />
                  </th>
                )}
                <th
                  style={{
                    textAlign: 'left',
                    padding: 'var(--space-3) var(--space-4)',
                    fontSize: 'var(--text-label)',
                    fontWeight: 'var(--weight-semibold)',
                    color: 'var(--text-secondary)',
                    borderBottom: 'var(--border-width) solid var(--border)',
                  }}
                >
                  Lokal
                </th>
                <th
                  style={{
                    textAlign: 'left',
                    width: 180,
                    padding: 'var(--space-3) var(--space-4)',
                    fontSize: 'var(--text-label)',
                    fontWeight: 'var(--weight-semibold)',
                    color: 'var(--text-secondary)',
                    borderBottom: 'var(--border-width) solid var(--border)',
                  }}
                >
                  Status
                </th>
                <th
                  style={{
                    textAlign: 'center',
                    width: 220,
                    whiteSpace: 'nowrap',
                    padding: 'var(--space-3) var(--space-4)',
                    fontSize: 'var(--text-label)',
                    fontWeight: 'var(--weight-semibold)',
                    color: 'var(--text-secondary)',
                    borderBottom: 'var(--border-width) solid var(--border)',
                  }}
                >
                  Akcje
                </th>
              </tr>
            </thead>
            <tbody>
              {building.apartments
                .sort((a, b) => parseInt(a.apartmentNumber) - parseInt(b.apartmentNumber))
                .map((apartment) => {
                  // JEDNA wartość na wiersz: kanon 5 statusów, a gdy lokalu jeszcze nie
                  // odwiedzono (canonicalStatus = null) — legacy „Nie zaplanowano"/„Zaplanowano".
                  // Bez tego „Brak uwag" i „Zalecenia" wyglądały identycznie („Wykonano"),
                  // bo legacy `completed` niesie oba (właściciel 2026-07-28: statusy mają być
                  // jednakowe w adminie i mobilce).
                  const displayStatus = apartment.canonicalStatus
                    ?? apartment.inspectionStatus
                    ?? 'not_scheduled';
                  const deleteBlocked = apartmentDeleteBlockReason(apartment);
                  const deleteTitle = deleteBlocked
                    ? (isSuperadmin ? 'Usuń lokal razem z zapisem kontroli (superadmin)' : deleteBlocked)
                    : 'Usuń lokal';
                  const deleteError = aptDeleteError?.apartmentId === apartment.id ? aptDeleteError.text : null;
                  return (
                  <React.Fragment key={apartment.id}>
                  <tr
                    style={{ borderTop: 'var(--border-width) solid var(--border)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {inspectionId && (
                      <td style={{ textAlign: 'center', padding: 'var(--space-3) var(--space-2)' }}>
                        <input
                          type="checkbox"
                          aria-label={`Zaznacz lokal ${apartment.apartmentNumber}`}
                          checked={selectedCards.has(apartment.apartmentNumber)}
                          onChange={() => toggleCardSelection(apartment.apartmentNumber)}
                        />
                      </td>
                    )}
                    <td style={{ padding: 'var(--space-3) var(--space-4)', minHeight: 64 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <span style={{ fontSize: 'var(--text-body)', fontWeight: 'var(--weight-medium)', color: 'var(--text-primary)' }}>
                          Lokal {apartment.apartmentNumber}
                        </span>
                        {/* E3: oznaczenie na drzwiach / klatka (tylko wyświetlanie; numer = CEEB). */}
                        <button
                          type="button"
                          title="Oznaczenie lokalu (klatka, drzwi)"
                          aria-label={`Oznaczenie lokalu ${apartment.apartmentNumber}`}
                          onClick={() => setLabelsFocus(apartment.id)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 32, height: 32, flex: 'none', padding: 0,
                            background: 'transparent', color: 'var(--text-secondary)',
                            border: 'none', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                          }}
                        >
                          <Icon name="pencil" size={16} />
                        </button>
                      </div>
                      {apartmentDoorMarking(apartment) && (
                        <div
                          data-testid={`apartment-marking-${apartment.id}`}
                          style={{ marginTop: '2px', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}
                        >
                          {apartmentDoorMarking(apartment)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                      {/* Etykieta i kolor z JEDNEJ mapy współdzielonej z mobilką (StatusMap.ts,
                          lustro backendu, pinowane parity-testem). Wcześniej stał tu ręczny
                          ternary z trzema przypadkami: WSZYSTKO poza completed/no_access —
                          w tym ODMOWA lokatora — renderowało się jako „Nie zaplanowano", więc
                          lokal po odmowie wyglądał w panelu na nieodwiedzony. */}
                      <StatusChip
                        status={statusToDS(displayStatus)}
                        label={statusLabel(displayStatus)}
                        size="sm"
                      />
                      {/* Data wizyty — przy KAŻDYM statusie niosącym wynik (także nieobecność
                          i odmowa; właściciel 2026-07-28). To dzień OSTATNIEJ WIZYTY, liczony
                          przez backend tym samym akcesorem, co kolumna „Przebieg" na PDF
                          (utils/inspectionCourse.js). Wcześniej szła tu data założenia rekordu
                          AIR: lokal 4 na Czaplach 24 pokazywał „Nieobecność · 07.07", choć
                          07.07 kominiarz WSZEDŁ, a nieobecność była 23.07. */}
                      {apartment.lastInspectionDate && hasVisitOutcome(displayStatus) && (
                        <div
                          data-testid={`apartment-date-${apartment.id}`}
                          style={{ marginTop: '2px', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}
                        >
                          {formatDate(apartment.lastInspectionDate)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'center', flexWrap: 'nowrap' }}>
                        {/* „Wykonaj przegląd" tylko gdy jest przegląd (W6 — read-only nie zapisuje kontroli lokalu). */}
                        {inspection && (
                        <button
                          type="button"
                          title="Wykonaj przegląd"
                          aria-label="Wykonaj przegląd"
                          onClick={() => handleStartInspection(apartment)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 44,
                            height: 44,
                            flex: 'none',
                            background: 'var(--action-primary)',
                            color: 'var(--text-on-action)',
                            border: 'none',
                            borderRadius: 'var(--radius-md)',
                            cursor: 'pointer',
                          }}
                        >
                          <Icon name="clipboard" size={22} />
                        </button>
                        )}
                        {/* Pobierz protokół lokalu (pojedynczo) — feedback właściciela 2026-07-24. */}
                        {inspectionId && (
                        <button
                          type="button"
                          title="Pobierz protokół lokalu (PDF)"
                          aria-label="Pobierz protokół lokalu"
                          disabled={cardsLoading}
                          onClick={() => handleDownloadProtocols([apartment.apartmentNumber])}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 44,
                            height: 44,
                            flex: 'none',
                            background: 'var(--surface-card)',
                            color: 'var(--action-primary)',
                            border: 'var(--border-width-strong) solid var(--border-strong)',
                            borderRadius: 'var(--radius-md)',
                            cursor: cardsLoading ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <Icon name="download" size={22} />
                        </button>
                        )}
                        <button
                          type="button"
                          title="Historia przeglądów"
                          aria-label="Historia przeglądów"
                          onClick={() => handleViewHistory(apartment)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 44,
                            height: 44,
                            flex: 'none',
                            background: 'var(--surface-card)',
                            color: 'var(--action-primary)',
                            border: 'var(--border-width-strong) solid var(--border-strong)',
                            borderRadius: 'var(--radius-md)',
                            cursor: 'pointer',
                          }}
                        >
                          <Icon name="clock" size={22} />
                        </button>
                        <button
                          type="button"
                          title="Historia zmian (audit log)"
                          aria-label="Historia zmian (audit log)"
                          onClick={() =>
                            setHistoryApartment({
                              id: apartment.id,
                              number: apartment.apartmentNumber,
                              address: `${building.street} ${building.houseNumber}, ${building.postalCode} ${building.city}`,
                            })
                          }
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 44,
                            height: 44,
                            flex: 'none',
                            background: 'var(--surface-card)',
                            color: 'var(--action-primary)',
                            border: 'var(--border-width-strong) solid var(--border-strong)',
                            borderRadius: 'var(--radius-md)',
                            cursor: 'pointer',
                          }}
                        >
                          <Icon name="circle-dot" size={22} />
                        </button>
                        {/* Kosz „zablokowany", gdy lokal ma zapis kontroli (backend i tak odda 409
                            APARTMENT_HAS_RECORDS) — parytet z tabletem. B5 (raport uwag Bronka 2026-10-19):
                            powód tylko w tooltipie był niewidoczny na tablecie/telefonie, więc klik pokazuje
                            go w wierszu pod lokalem (ten sam komunikat co odmowa serwera), bez pytania o usunięcie. */}
                        {/* Superadmin: kosz przy lokalu z zapisem otwiera „Usuń lokal razem z zapisem kontroli" (F8). */}
                        <span title={deleteTitle} style={{ display: 'inline-flex', flex: 'none' }}>
                        <button
                          type="button"
                          title={deleteTitle}
                          aria-label="Usuń lokal"
                          aria-disabled={!!deleteBlocked && !isSuperadmin}
                          data-testid={`apartment-delete-${apartment.id}`}
                          onClick={() => {
                            if (deleteBlocked && isSuperadmin) {
                              setAptDeleteError(null);
                              setAptDeleteWithRecordsId(apartment.id);
                            } else if (deleteBlocked) setAptDeleteError({ apartmentId: apartment.id, text: `${deleteBlocked}.` });
                            else handleRemoveApartment(apartment.id);
                          }}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 44,
                            height: 44,
                            flex: 'none',
                            background: deleteBlocked ? 'var(--action-disabled-bg)' : 'var(--surface-card)',
                            color: deleteBlocked ? 'var(--action-disabled-text)' : 'var(--status-deny)',
                            border: 'var(--border-width-strong) solid var(--border-strong)',
                            borderRadius: 'var(--radius-md)',
                            cursor: deleteBlocked && !isSuperadmin ? 'not-allowed' : 'pointer',
                          }}
                          onMouseEnter={(e) => { if (!deleteBlocked) e.currentTarget.style.background = 'var(--status-deny-bg)'; }}
                          onMouseLeave={(e) => { if (!deleteBlocked) e.currentTarget.style.background = 'var(--surface-card)'; }}
                        >
                          <Icon name="trash" size={22} />
                        </button>
                        </span>
                      </div>
                    </td>
                  </tr>
                  {deleteError && (
                    <tr data-testid={`apartment-delete-error-${apartment.id}`}>
                      <td colSpan={inspectionId ? 4 : 3} style={{ padding: '0 var(--space-4) var(--space-3)' }}>
                        <div
                          role="alert"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-3)',
                            padding: 'var(--space-2) var(--space-3)',
                            borderRadius: 'var(--radius-md)',
                            fontFamily: 'var(--font-sans)',
                            fontSize: 'var(--text-label)',
                            background: 'var(--status-deny-bg)',
                            color: 'var(--status-deny)',
                            borderLeft: 'var(--bar-width) solid var(--status-deny-bar)',
                          }}
                        >
                          <Icon name="triangle-alert" size={18} />
                          <span style={{ flex: 1 }}>{deleteError}</span>
                          <button
                            type="button"
                            aria-label="Zamknij komunikat"
                            onClick={() => setAptDeleteError(null)}
                            style={{
                              display: 'inline-flex',
                              background: 'transparent',
                              border: 'none',
                              color: 'inherit',
                              cursor: 'pointer',
                              padding: 'var(--space-1)',
                            }}
                          >
                            <Icon name="x" size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal dodawania źródła ciepła */}
      {/* Dane właściciela / zarządcy (035, issue #167). Bez walidacji — parytet z mobilką,
          która też jej nie ma; pusty string czyści pole. */}
      <ModalShell
        open={showOwnerModal}
        title="Dane właściciela / zarządcy"
        width={520}
        onClose={() => setShowOwnerModal(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowOwnerModal(false)} disabled={ownerSaving}>
              Anuluj
            </Button>
            <Button variant="primary" onClick={handleSaveOwnerData} disabled={ownerSaving}>
              {ownerSaving ? 'Zapisywanie…' : 'Zapisz'}
            </Button>
          </>
        }
      >
        <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>
          Trafiają na 3. stronę protokołu CEEB. Wszystkie pola są opcjonalne — puste zostaje puste,
          a bot nie nadpisze tego, co już jest w CEEB.
        </div>

        {ownerError && (
          <div
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-3) var(--space-4)',
              marginBottom: 'var(--space-4)',
              background: 'var(--status-deny-bg)',
              border: 'var(--border-width) solid var(--status-deny-bar)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-label)',
              color: 'var(--status-deny)',
            }}
          >
            <Icon name="triangle-alert" size={18} />
            <span>{ownerError}</span>
          </div>
        )}

        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          {/* Zarządca (natywny select — brak DS Select; wzorzec AddBuildingModal) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label
              htmlFor="owner-modal-client"
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-label)',
                fontWeight: 'var(--weight-semibold)',
                lineHeight: 'var(--leading-snug)',
                color: 'var(--text-secondary)',
              }}
            >
              Zarządca
            </label>
            <select
              id="owner-modal-client"
              value={ownerForm.clientId}
              disabled={ownerSaving}
              onChange={(e) => setOwnerForm({ ...ownerForm, clientId: e.target.value })}
              style={{
                width: '100%',
                minHeight: 'var(--touch-min)',
                padding: '0 var(--space-4)',
                boxSizing: 'border-box',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-body)',
                color: 'var(--text-primary)',
                background: 'var(--surface-card)',
                border: 'var(--border-width-strong) solid var(--border-strong)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <option value="">— bez zarządcy —</option>
              {zarzadcy.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <InputField
            label="Nazwisko / nazwa firmy"
            value={ownerForm.ownerSurname}
            placeholder="np. Kowalski Jan / Wspólnota Mieszkaniowa…"
            onChange={(e) => setOwnerForm({ ...ownerForm, ownerSurname: e.target.value })}
          />
          <InputField
            label="Imię / nazwa skrócona firmy"
            value={ownerForm.ownerName}
            placeholder="np. Jan / WM Czapli 24"
            onChange={(e) => setOwnerForm({ ...ownerForm, ownerName: e.target.value })}
          />
          <InputField
            label="Adres e-mail"
            type="email"
            value={ownerForm.ownerEmail}
            placeholder="np. kontakt@wspolnota.pl"
            onChange={(e) => setOwnerForm({ ...ownerForm, ownerEmail: e.target.value })}
          />
        </div>
      </ModalShell>

      {editSource && (
        <HeatSourceEditModal
          key={editFormKey}
          open
          source={editSource}
          saving={editSaving}
          error={editError}
          onCancel={closeEditSource}
          onSave={handleSaveSourceParams}
        />
      )}

      <ModalShell
        open={showAddHeatSourceModal}
        title="Dodaj źródło ciepła"
        width={600}
        onClose={() => {
          setShowAddHeatSourceModal(false);
          setHeatSourceType('');
          setHeatCharacter('');
        }}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddHeatSourceModal(false);
                setHeatSourceType('');
                setHeatCharacter('');
              }}
            >
              Anuluj
            </Button>
            <Button variant="primary" icon="plus" onClick={handleAddHeatSource}>
              Dodaj
            </Button>
          </>
        }
      >
        <div style={{ fontSize: 'var(--text-label)', fontWeight: 'var(--weight-semibold)', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>
          Typ źródła ciepła:
        </div>
        <div style={{ display: 'grid', gap: 'var(--space-2)', marginBottom: 'var(--space-5)' }}>
          {heatSourceTypes.map((type) => {
            const active = heatSourceType === type.value;
            return (
              <button
                key={type.value}
                type="button"
                onClick={() => setHeatSourceType(type.value)}
                style={{
                  minWidth: 0,
                  padding: 'var(--space-3)',
                  textAlign: 'center',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-label)',
                  fontWeight: active ? 'var(--weight-semibold)' : 'var(--weight-regular)',
                  borderRadius: 'var(--radius-sm)',
                  border: `var(--border-width-strong) solid ${active ? 'var(--action-primary)' : 'var(--border)'}`,
                  background: active ? 'var(--changed-bg)' : 'var(--surface-fill)',
                  color: active ? 'var(--changed-text)' : 'var(--text-primary)',
                }}
              >
                {type.label}
              </button>
            );
          })}
        </div>

        {/* Typy bez „Charakteru” w CEEB (HEAT_TYPES_WITHOUT_CHARACTER) — bez tego wyboru */}
        {!HEAT_TYPES_WITHOUT_CHARACTER.includes(heatSourceType) && (
          <>
        <div style={{ fontSize: 'var(--text-label)', fontWeight: 'var(--weight-semibold)', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>
          Charakter ciepła:
        </div>
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          {heatCharacterTypes.map((char) => {
            const active = heatCharacter === char.value;
            return (
              <button
                key={char.value}
                type="button"
                onClick={() => setHeatCharacter(char.value)}
                style={{
                  minWidth: 0,
                  padding: 'var(--space-3)',
                  textAlign: 'center',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-label)',
                  fontWeight: active ? 'var(--weight-semibold)' : 'var(--weight-regular)',
                  borderRadius: 'var(--radius-sm)',
                  border: `var(--border-width-strong) solid ${active ? 'var(--action-primary)' : 'var(--border)'}`,
                  background: active ? 'var(--changed-bg)' : 'var(--surface-fill)',
                  color: active ? 'var(--changed-text)' : 'var(--text-primary)',
                }}
              >
                {char.label}
              </button>
            );
          })}
        </div>
          </>
        )}
      </ModalShell>

      {/* Modal dodawania lokalu */}
      <ModalShell
        open={showAddApartmentModal}
        title="Dodaj nowy lokal"
        width={480}
        onClose={() => {
          setShowAddApartmentModal(false);
          setNewApartmentNumber('');
        }}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddApartmentModal(false);
                setNewApartmentNumber('');
              }}
            >
              Anuluj
            </Button>
            <Button variant="primary" icon="plus" onClick={handleAddApartment}>
              Dodaj
            </Button>
          </>
        }
      >
        <InputField
          label="Numer lokalu:"
          type="text"
          value={newApartmentNumber}
          onChange={(e) => setNewApartmentNumber(e.target.value)}
          placeholder="np. 4"
          autoFocus
        />
      </ModalShell>

      {/* Modal wykonania przeglądu — wymaga przeglądu (W6 read-only: inspectionId=null → niedostępny) */}
      {showInspectionModal && editingApartment && building && inspectionId && (
        <ApartmentInspectionForm
          apartmentId={editingApartment.id}
          apartmentNumber={editingApartment.apartmentNumber}
          buildingId={buildingId}
          buildingAddress={`${building.street} ${building.houseNumber}, ${building.postalCode} ${building.city}`}
          inspectionId={inspectionId}
          onClose={() => {
            setShowInspectionModal(false);
            setEditingApartment(null);
          }}
          onSave={async () => {
            try {
              // Pobierz dane przeglądu
              const records = await apartmentInspectionsApi.getByApartmentId(editingApartment.id);
              const currentRecord = records.find((r: any) => r.inspectionId === inspectionId);

              if (currentRecord && currentRecord.inspectionDate) {
                // Mapuj status z przeglądu na status lokalu
                const newStatus = mapInspectionStatusToApartmentStatus(currentRecord.inspectionStatus);
                const inspectionDate = currentRecord.inspectionDate;

                console.log('🔄 [onSave] Aktualizacja statusu lokalu:', {
                  apartmentId: editingApartment.id,
                  newStatus,
                  inspectionDate
                });

                // Zaktualizuj status apartmentu BEZ regenerowania UUID
                await apartmentsApi.updateStatus(editingApartment.id, {
                  inspectionStatus: newStatus,
                  lastInspectionDate: inspectionDate
                });

                console.log('✅ [onSave] Status zaktualizowany pomyślnie');
              }
            } catch (error) {
              console.error('❌ [onSave] Błąd podczas aktualizacji statusu:', error);
            }

            // Odśwież dane budynku po zapisaniu przeglądu
            loadData();
          }}
        />
      )}

      {/* Modal historii przeglądów */}
      <ModalShell
        open={showHistoryModal && !!editingApartment}
        title={editingApartment ? `Historia przeglądów - Lokal ${editingApartment.apartmentNumber}` : 'Historia przeglądów'}
        width={800}
        onClose={() => setShowHistoryModal(false)}
        footer={
          <Button variant="primary" onClick={() => setShowHistoryModal(false)}>
            Zamknij
          </Button>
        }
      >
        {/* Dodatkowe terminy przy BUDYNKU (III, IV…) — widoczne też tutaj (są wspólne dla
            lokali danego przeglądu, ale tester nie widział ich w historii lokalu). */}
        {sortedAdditionalTerms.length > 0 && (
          <div style={{ marginBottom: 'var(--space-5)' }}>
            <div
              style={{
                fontSize: 'var(--text-label)',
                fontWeight: 'var(--weight-semibold)',
                color: 'var(--text-secondary)',
                marginBottom: 'var(--space-3)',
              }}
            >
              Dodatkowe terminy budynku
            </div>
            <div style={{ border: 'var(--border-width) solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
              {sortedAdditionalTerms.map(({ term, originalIndex, number }, idx) => (
                <div
                  key={originalIndex}
                  style={{
                    padding: 'var(--space-3) var(--space-4)',
                    borderBottom:
                      idx < sortedAdditionalTerms.length - 1 ? 'var(--border-width) solid var(--border)' : 'none',
                  }}
                >
                  <div
                    style={{
                      fontSize: 'var(--text-body)',
                      fontWeight: 'var(--weight-semibold)',
                      color: 'var(--text-primary)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {romanTerm(number) ?? '—'} termin: {formatDate(term.date)}
                  </div>
                  {term.reason && (
                    <div style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', marginTop: 4 }}>
                      {term.reason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {inspectionHistory.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-8) var(--space-4)',
              textAlign: 'center',
              color: 'var(--text-secondary)',
            }}
          >
            <Icon name="clipboard" size={40} color="var(--text-muted)" />
            <span style={{ fontSize: 'var(--text-body)' }}>Brak zapisanych przeglądów dla tego lokalu</span>
          </div>
        ) : (
          <div>
            {inspectionHistory.map((record, index) => {
              const isAttemptRecord =
                record.inspectionStatus === 'brak_dostepu' && record.attempts && record.attempts.length > 0;
              const dsStatus = statusToDS(record.inspectionStatus);
              return (
                <div
                  key={index}
                  style={{
                    background: isAttemptRecord ? 'var(--status-deny-bg)' : 'var(--surface-fill)',
                    padding: 'var(--space-5)',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: 'var(--space-4)',
                    border: 'var(--border-width) solid var(--border)',
                    borderLeft: isAttemptRecord
                      ? `var(--bar-width) solid var(--status-deny-bar)`
                      : `var(--border-width) solid var(--border)`,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
                    <div>
                      <strong style={{ fontSize: 'var(--text-body)', color: 'var(--text-primary)' }}>
                        Protokół: {record.protocolNumber}
                      </strong>
                      <div style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', marginTop: 4 }}>
                        {isAttemptRecord ? 'Ostatnia aktualizacja' : 'Data przeglądu'}: {formatDate(record.inspectionDate)}
                      </div>
                      {record.savedBy && (
                        <div style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
                          Wykonał: {record.savedBy}
                        </div>
                      )}
                    </div>
                    {/* `label` jest OBOWIĄZKOWY: bez niego StatusChip bierze własną domyślną
                        etykietę (STATUS_META), przez co ten sam lokal miał „Brak dostępu" tutaj
                        i „Nieobecność" na liście lokali. Kanon etykiet to STATUS_LABELS
                        z ds/StatusMap.ts (lustro backendu, pinowane parity-testem). QA 2026-08-05, S3. */}
                    <StatusChip
                      status={dsStatus}
                      label={statusLabel(record.inspectionStatus)}
                      size="sm"
                      style={{ flex: 'none' }}
                    />
                  </div>

                  {record.specialDesignation && (
                    <div style={{ marginBottom: 'var(--space-2)', fontSize: 'var(--text-label)', color: 'var(--text-primary)' }}>
                      <strong>Oznaczenie specjalne:</strong> {record.specialDesignation}
                    </div>
                  )}

                  {record.attempts && record.attempts.length > 0 && (
                    <div style={{ marginBottom: 'var(--space-2)', fontSize: 'var(--text-label)', color: 'var(--text-primary)' }}>
                      <strong>Próby dostępu ({record.attempts.length}):</strong>
                      {/* Tolerancja OBU kształtów attempts (canonical/legacy); czas tylko gdy realny timestamp.
                          Etykieta = NUMER TERMINU budynku (2026-07-28), nie „która próba" —
                          `termNumber` znaczy teraz termin, a nie licznik pukań do tych drzwi. */}
                      {[...record.attempts]
                        .sort((a: any, b: any) => attemptSortKey(a) - attemptSortKey(b))
                        .map((attempt: any, i: number) => {
                          const time = formatAttemptTime(attempt);
                          const roman = romanTerm(attemptNumber(attempt, i));
                          // WYNIK próby — bez niego historia mówiła tylko „byłem", nie „i co z tego":
                          // wizyta zakończona brakiem uwag wyglądała identycznie jak nieobecność
                          // (PDF podsumowania drukował wynik od zawsze). Etykieta z lustra
                          // statusów, nie z ręcznej mapy. Stare wpisy (pre-021) statusu nie mają
                          // — wtedy nic nie dopisujemy zamiast zgadywać.
                          const outcome = attempt?.status ? statusLabel(attempt.status) : null;
                          return (
                            <div key={i} style={{ marginLeft: 'var(--space-4)', marginTop: 'var(--space-2)', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
                              • {roman ? `${roman} termin` : 'Wizyta'} · {formatAttemptDate(attempt)}{time ? ` o ${time}` : ''}
                              {outcome ? ` — ${outcome}` : ''}
                            </div>
                          );
                        })}
                    </div>
                  )}

                  {record.recommendations && (
                    <div style={{ fontSize: 'var(--text-label)', color: 'var(--text-primary)' }}>
                      <strong>Zalecenia:</strong>
                      {/* Termin powstania zalecenia (migr. 039, właściciel 2026-07-27: „dodać przy
                          lokalu termin, w którym zalecenia zostały dodane, jeżeli to było w trzecim
                          terminie"). Rekordy sprzed migracji nie mają tych danych — wtedy nic nie
                          pokazujemy zamiast zmyślać termin. */}
                      {record.recommendationTermNumber != null && (
                        <span
                          data-testid={`rec-term-${record.id}`}
                          style={{ marginLeft: 'var(--space-2)', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}
                        >
                          ({romanTerm(record.recommendationTermNumber)} termin
                          {record.recommendationTermDate ? `, ${formatDate(record.recommendationTermDate)}` : ''})
                        </span>
                      )}
                      <div style={{ marginLeft: 'var(--space-4)', marginTop: 'var(--space-2)', color: 'var(--text-secondary)' }}>
                        {record.recommendations}
                      </div>
                    </div>
                  )}

                  {record.savedAt && (
                    <div
                      style={{
                        marginTop: 'var(--space-4)',
                        fontSize: 'var(--text-caption)',
                        color: 'var(--text-muted)',
                        borderTop: 'var(--border-width) solid var(--border)',
                        paddingTop: 'var(--space-2)',
                      }}
                    >
                      Zapisano: {formatDate(record.savedAt)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ModalShell>

      <ApartmentHistoryModal
        open={historyApartment !== null}
        onClose={() => setHistoryApartment(null)}
        apartmentId={historyApartment?.id ?? null}
        apartmentNumber={historyApartment?.number}
        buildingAddress={historyApartment?.address}
      />

      <ApartmentDeleteWithRecordsModal
        apartmentId={aptDeleteWithRecordsId}
        labelParts={building.apartments.find((a) => a.id === aptDeleteWithRecordsId) ?? null}
        onClose={() => setAptDeleteWithRecordsId(null)}
        onRefused={(apartmentId, text) => setAptDeleteError({ apartmentId, text })}
        onDeleted={async (apartmentNumber, recordsDeleted) => {
          setAptDeleteToast(`Usunięto lokal ${apartmentNumber} razem z zapisem kontroli (${recordsDeleted}).`);
          try {
            setBuilding(await buildingsApi.getById(buildingId));
          } catch (error) {
            console.error('Error reloading building after apartment delete:', error);
            setAptDeleteToast(`Usunięto lokal ${apartmentNumber}. Odśwież widok, żeby zobaczyć aktualną listę lokali.`);
          }
        }}
      />

      {/* Toast po usunięciu lokalu (wiersz znika, więc komunikat nie może stać przy nim). */}
      {aptDeleteToast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--space-6)',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            maxWidth: 'min(92vw, 560px)',
            padding: 'var(--space-3) var(--space-4)',
            background: 'var(--status-ok-bg)',
            border: 'var(--border-width) solid var(--border-strong)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-float)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-label)',
            color: 'var(--status-ok)',
          }}
        >
          <Icon name="check" size={18} color="var(--status-ok)" />
          <span style={{ flex: 1 }}>{aptDeleteToast}</span>
          <button
            type="button"
            aria-label="Zamknij powiadomienie"
            onClick={() => setAptDeleteToast(null)}
            style={{
              display: 'inline-flex',
              background: 'transparent',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              padding: 'var(--space-1)',
            }}
          >
            <Icon name="x" size={16} />
          </button>
        </div>
      )}

      <UncheckedApartmentsModal
        buildingIds={showUnchecked ? [buildingId] : null}
        subject={`${building.street} ${building.houseNumber}, ${building.city}`}
        onClose={() => setShowUnchecked(false)}
      />

      {inspectionId && (
        <BulkAbsenceModal
          apartments={bulkAbsenceFor}
          inspectionId={inspectionId}
          terms={inspection?.terms}
          subject={`${building.street} ${building.houseNumber}`}
          onClose={() => {
            setBulkAbsenceFor(null);
            // Odświeżenie dopiero po zamknięciu: loadData pokazuje ekran ładowania i zdjęłoby podsumowanie.
            if (bulkAbsenceSavedRef.current) {
              bulkAbsenceSavedRef.current = false;
              void loadData();
            }
          }}
          onDone={({ saved }) => {
            bulkAbsenceSavedRef.current = true;
            if (saved) setSelectedCards(new Set());
          }}
        />
      )}

      <ApartmentLabelsModal
        apartments={labelsFocus !== undefined ? building.apartments : null}
        buildingId={buildingId}
        focusApartmentId={labelsFocus ?? null}
        subject={`${building.street} ${building.houseNumber}`}
        onClose={() => setLabelsFocus(undefined)}
        onSaved={() => { void loadData(); }}
      />

      <ApartmentRenumberModal
        apartments={renumberOpen ? building.apartments : null}
        buildingId={buildingId}
        subject={`${building.street} ${building.houseNumber}`}
        onClose={() => setRenumberOpen(false)}
        onSaved={() => { void loadData(); }}
      />

      {/* DS confirm-modal — replaces window.confirm for destructive actions */}
      <ConfirmModal
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        confirmLabel={confirmState.confirmLabel ?? 'Usuń'}
        cancelLabel="Anuluj"
        destructive
        onConfirm={confirmState.onConfirm}
        onCancel={closeConfirm}
      />
    </ModalShell>
  );
};

export default BuildingInspectionView;
